import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useSearchParams } from 'react-router-dom';
import {
  AspectRatio,
  Box,
  Button,
  ButtonGroup,
  Card,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Link,
  Icon,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  VStack,
  Badge,
  Alert,
  Checkbox,
  Image as ChakraImage,
  NativeSelect,
  Stack,
  RadioGroup,
  useDisclosure,
  Tabs,
  Table,
  Tag,
  Wrap,
  Slider,
  Dialog,
  Portal,
  Separator,
  Field,
} from '@chakra-ui/react';
import { Radio, TabList, Tab, TabPanels, TabPanel, Thead, Tbody, Tr, Th, Td, SliderTrack, SliderThumb, SliderFilledTrack, SliderRoot, Switch, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { CheckboxGroup, MultiCheckboxField } from '@/components/ui/multi-checkbox-field';
import { toaster } from '@/components/ui/toaster';
import {
  FaMusic,
  FaPlus,
  FaTrash,
  FaSync,
  FaSave,
  FaLayerGroup,
  FaList,
  FaTh,
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaSearchPlus,
  FaSearchMinus,
  FaFolder,
  FaDownload,
  FaShareAlt,
  FaEye,
  FaKey,
  FaCopy,
  FaCheckCircle,
  FaWhatsapp,
  FaQrcode,
  FaYoutube,
  FaEdit,
  FaFilePdf,
  FaFileExcel,
  FaTimes,
} from 'react-icons/fa';
import { normalizeYouTubeWatchUrl } from '../utils/youtubeUrl';
import BandaRepertorioObraFicha from './BandaRepertorioObraFicha';
import BandaRepertorioCarpetaFicha from './BandaRepertorioCarpetaFicha';
import BandaRepertorioCarpetaObrasModal from './BandaRepertorioCarpetaObrasModal';
import { downloadBlob, generateRepertorioShareQrPng } from '../utils/repertorioShareQr';
import { downloadPartituraPaginaImage } from '../utils/downloadPartituraPagina';
import { buildPartituraDownloadFilename } from '../utils/partituraDownloadFilename';
import { arrayMove } from '@dnd-kit/sortable';
import AppLayout from './AppLayout.jsx';
import BandaRepertorioEtiquetadoPaginasModal from './BandaRepertorioEtiquetadoPaginasModal.jsx';
import BandaRepertorioShareIncidencias from './BandaRepertorioShareIncidencias.jsx';
import BandaRepertorioEstadisticas from './BandaRepertorioEstadisticas.jsx';
import BandaRepertorioAltaObraWizard from './BandaRepertorioAltaObraWizard.jsx';
import BandaRepertorioAnadirPaginasWizard from './BandaRepertorioAnadirPaginasWizard.jsx';
import { useAuth } from '../contexts/AuthContext';
import { bandasAPI, instrumentosAPI } from '../services/api';
import { etiquetaSlugDesdeNombre, paginaInstrumentoCoincideFiltro } from '../utils/instrumentoEtiqueta';
import { formatMusicoInstrumentosText } from '../utils/musicoInstrumentos';
import { pageImageSrc } from '../utils/staticUrl';
import { useProductTour } from '../hooks/useProductTour';
import { bandaRepertorioSteps } from '../tours/bandaRepertorioTour';
import {
  downloadObrasListadoExcel,
  downloadObrasListadoPdfExhibicion,
  downloadObrasListadoPdfGestion,
} from '../utils/exportObrasListado';

const ROTATIONS = [0, 90, 180, 270];

/** Valor del `<Select>` para descargar todas las páginas de cada obra (sin filtrar por instrumento). */
const PARTICHELLAS_INSTR_TODAS = '__TODAS__';

const FAMILIA_INSTRUMENTO_LABELS = {
  VIENTO_METAL: 'Viento metal',
  VIENTO_MADERA: 'Viento madera',
  PERCUSION: 'Percusión',
  CUERDA: 'Cuerda',
  VOZ: 'Voz',
  OTRO: 'Otro',
};

function humanizeSlugLabel(slug) {
  const s = String(slug || '').trim();
  if (!s) return '';
  return s
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

/** Dígitos internacionales para wa.me (sin +). null si el teléfono no es usable. */
function whatsappE164DigitsFromTelefono(raw) {
  if (typeof raw !== 'string') return null;
  let s = raw.replace(/[\s().-]/g, '');
  if (!s) return null;
  if (s.startsWith('00')) s = s.slice(2);
  if (s.startsWith('+')) s = s.slice(1);
  if (/^\d{9}$/.test(s) && s[0] !== '0') s = `34${s}`;
  if (!/^\d{10,15}$/.test(s)) return null;
  return s;
}

/** La API puede devolver el array en la raíz o envuelto (p. ej. `{ data: [...] }`). */
function asInstrumentosPersonalizadosList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload == null || typeof payload !== 'object') return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.instrumentos)) return payload.instrumentos;
  if (payload.data != null && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    return asInstrumentosPersonalizadosList(payload.data);
  }
  return [];
}

/**
 * Interpreta órdenes de página: "2-26", "2-5,7-8" o un solo "5".
 * Comprueba que cada orden exista en `paginas`.
 */
function parseTramosOrdenes(texto, paginas) {
  const ordExistentes = new Set((paginas || []).map((p) => p.orden));
  const trimmed = (texto || '').trim();
  if (!trimmed) {
    return { ok: false, error: 'Indica al menos un tramo (p. ej. 2-26 o 2-5, 7-8).', tramos: [] };
  }
  const partes = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  const tramos = [];
  for (const p of partes) {
    const single = p.match(/^(\d+)$/);
    if (single) {
      const n = parseInt(single[1], 10);
      tramos.push({ desde: n, hasta: n });
      continue;
    }
    const pair = p.match(/^(\d+)\s*-\s*(\d+)$/);
    if (!pair) {
      return {
        ok: false,
        error: `Tramo no válido: «${p}». Usa «inicio-fin», un solo número o varios tramos separados por comas.`,
        tramos: [],
      };
    }
    let a = parseInt(pair[1], 10);
    let b = parseInt(pair[2], 10);
    if (a > b) [a, b] = [b, a];
    tramos.push({ desde: a, hasta: b });
  }
  for (const { desde, hasta } of tramos) {
    for (let o = desde; o <= hasta; o++) {
      if (!ordExistentes.has(o)) {
        return { ok: false, error: `No existe página con orden ${o} en esta obra.`, tramos: [] };
      }
    }
  }
  return { ok: true, error: '', tramos };
}

/** Cuerpo PATCH de una página a partir del objeto en memoria (`detail.paginas`). */
function buildObraPartituraPaginaPatch(p) {
  return {
    rotacion: Number(p?.rotacion) || 0,
    instrumentosVoces: Array.isArray(p?.instrumentosVoces) ? [...p.instrumentosVoces] : [],
    tituloObra: p?.tituloObra?.trim() || null,
    autor: p?.autor?.trim() || null,
    notasDirector: p?.notasDirector?.trim() || null,
    ocrTexto: p?.ocrTexto?.trim() || null,
  };
}

export default function BandaRepertorio() {
  const { profile, user } = useAuth();
  
  useProductTour('banda-repertorio', bandaRepertorioSteps, { autoStart: false });
  const cardBg = useColorModeValue('white', 'gray.800');
  const filterPanelBg = useColorModeValue('gray.50', 'gray.900');
  const [loading, setLoading] = useState(true);
  const [obras, setObras] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  /** 'ficha' = vista lectura; 'edit' = formulario de edición. */
  const [obraViewMode, setObraViewMode] = useState('ficha');
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [obraMetaDirty, setObraMetaDirty] = useState(false);
  const [instrumentCatalog, setInstrumentCatalog] = useState([]);
  const [altaObraWizardOpen, setAltaObraWizardOpen] = useState(false);
  const [anadirPaginasWizardOpen, setAnadirPaginasWizardOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [vocesCatalog, setVocesCatalog] = useState([]);
  const [nuevaVozNombre, setNuevaVozNombre] = useState('');
  const [vocesMutating, setVocesMutating] = useState(false);
  const [generosCatalog, setGenerosCatalog] = useState([]);
  const [ambitoPagina, setAmbitoPagina] = useState('individual');
  const [rangoOrdenesTexto, setRangoOrdenesTexto] = useState('');
  const [rangoPatch, setRangoPatch] = useState({
    instrumentosVoces: [],
    notasDirector: '',
  });
  const [visorPartituraAbierto, setVisorPartituraAbierto] = useState(false);
  const [descargandoPaginaId, setDescargandoPaginaId] = useState(null);
  const [descargandoObraCompleta, setDescargandoObraCompleta] = useState(false);
  const [carpetasCatalog, setCarpetasCatalog] = useState([]);
  const [carpetaFiltroId, setCarpetaFiltroId] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [archivoMusicaTabIndex, setArchivoMusicaTabIndex] = useState(0);
  /** 0 = enlace y músicos, 1 = incidencias (dentro de Compartir repertorio). */
  const [compartirSubTabIndex, setCompartirSubTabIndex] = useState(0);
  const [instrumentosPersonalizados, setInstrumentosPersonalizados] = useState([]);
  const [nuevoInstrumentoNombre, setNuevoInstrumentoNombre] = useState('');
  const [nuevoInstrumentoFamilia, setNuevoInstrumentoFamilia] = useState('OTRO');
  const [instPersMutating, setInstPersMutating] = useState(false);
  const [nuevaCarpetaNombre, setNuevaCarpetaNombre] = useState('');
  const [nuevaCarpetaDescripcion, setNuevaCarpetaDescripcion] = useState('');
  const [carpetasMutating, setCarpetasMutating] = useState(false);
  const [carpetasSearch, setCarpetasSearch] = useState('');
  const [carpetasSort, setCarpetasSort] = useState('nombre_asc'); // nombre_asc | nombre_desc | obras_desc | obras_asc
  const [carpetasPage, setCarpetasPage] = useState(1);
  const [carpetasPageSize, setCarpetasPageSize] = useState(10);
  const [drawerCarpetaObras, setDrawerCarpetaObras] = useState(null);
  /** Ids de obras en la carpeta en orden de interpretación (mismo orden que se guarda en backend). */
  const [carpetaDrawerOrdenIds, setCarpetaDrawerOrdenIds] = useState([]);
  const [carpetaDrawerSearch, setCarpetaDrawerSearch] = useState('');
  const [carpetaDrawerGeneroId, setCarpetaDrawerGeneroId] = useState('');
  /** '' = todos los estados */
  const [carpetaDrawerEstado, setCarpetaDrawerEstado] = useState('');
  const [selectedCarpetaId, setSelectedCarpetaId] = useState(null);
  /** 'ficha' = vista lectura; 'edit' = formulario de edición. */
  const [carpetaViewMode, setCarpetaViewMode] = useState('ficha');
  const [renombrarCarpetaTexto, setRenombrarCarpetaTexto] = useState('');
  const [renombrarCarpetaDescripcion, setRenombrarCarpetaDescripcion] = useState('');
  const [obraSearch, setObraSearch] = useState('');
  const [obraGeneroFilterId, setObraGeneroFilterId] = useState('');
  /** '' = todos los estados */
  const [obraEstadoFilter, setObraEstadoFilter] = useState('');
  /** 'obras' (defecto) | 'paginas' — modo de listado en Biblioteca */
  const [bibliotecaModo, setBibliotecaModo] = useState('obras');
  const [paginaInstrumentoFilter, setPaginaInstrumentoFilter] = useState('');
  const [paginasBiblioteca, setPaginasBiblioteca] = useState([]);
  const [paginasBibliotecaLoading, setPaginasBibliotecaLoading] = useState(false);
  const [visorNavegacionPaginas, setVisorNavegacionPaginas] = useState([]);
  const [obraVista, setObraVista] = useState(() => {
    try {
      const v = localStorage.getItem('cadencya-repertorio-obras-vista');
      return v === 'grid' ? 'grid' : 'list';
    } catch {
      return 'list';
    }
  });
  const [obraPage, setObraPage] = useState(1);
  const [obraPageSize, setObraPageSize] = useState(25);
  const [modalPartichellasAbierto, setModalPartichellasAbierto] = useState(false);
  const [partichellasInstrumentSlug, setPartichellasInstrumentSlug] = useState('');
  const [partichellasVocesPreview, setPartichellasVocesPreview] = useState([]);
  const [partichellasVocesSel, setPartichellasVocesSel] = useState([]);
  const [partichellasPreviewLoading, setPartichellasPreviewLoading] = useState(false);
  const [partichellasPdfLoading, setPartichellasPdfLoading] = useState(false);
  const [visorZoom, setVisorZoom] = useState(1);
  /** Controles flotantes del visor (en móvil se ocultan hasta un toque). */
  const [visorChromeVisible, setVisorChromeVisible] = useState(false);
  const [visorImgNatural, setVisorImgNatural] = useState({ w: 0, h: 0 });
  const [isMobileVisor, setIsMobileVisor] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.matchMedia('(max-width: 767px), (hover: none) and (pointer: coarse)').matches;
    } catch {
      return true;
    }
  });
  const visorScrollRef = useRef(null);
  const visorViewportRef = useRef(null);
  const visorPinchRef = useRef({ active: false, startDist: 0, startZoom: 1 });
  const visorDragRef = useRef({ down: false, startX: 0, startY: 0, startScrollLeft: 0, startScrollTop: 0 });
  const visorSwipeRef = useRef({
    tracking: false,
    startX: 0,
    startY: 0,
    startTime: 0,
    moved: false,
    axis: null,
    lastDx: 0,
    lastDy: 0,
  });
  const [visorViewportSize, setVisorViewportSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px), (hover: none) and (pointer: coarse)');
    const apply = () => setIsMobileVisor(Boolean(mq.matches));
    apply();
    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  const [repertorioShare, setRepertorioShare] = useState({
    shareUrl: '',
    hasPassword: false,
    canRevealSharePassword: false,
  });
  const [repertorioShareLoading, setRepertorioShareLoading] = useState(true);
  const [generandoQrRepertorio, setGenerandoQrRepertorio] = useState(false);
  const [qrPwdAccount, setQrPwdAccount] = useState('');
  const [qrPwdLoading, setQrPwdLoading] = useState(false);
  const [youtubeUrlDraft, setYoutubeUrlDraft] = useState('');
  const [youtubeSaving, setYoutubeSaving] = useState(false);
  const [musicoEnlaces, setMusicoEnlaces] = useState({ enabled: false, musicos: [] });
  const [musicoEnlacesLoading, setMusicoEnlacesLoading] = useState(false);
  const { open: modalShareOpen, onOpen: onModalShareOpen, onClose: onModalShareClose } = useDisclosure();
  const { open: modalVerPwdOpen, onOpen: onModalVerPwdOpen, onClose: onModalVerPwdClose } = useDisclosure();
  const { open: modalCambiarPwdOpen, onOpen: onModalCambiarPwdOpen, onClose: onModalCambiarPwdClose } = useDisclosure();
  const { open: modalEtiquetadoPaginasOpen, onOpen: onModalEtiquetadoPaginasOpen, onClose: onModalEtiquetadoPaginasClose } =
    useDisclosure();
  const { open: dialogListoOpen, onOpen: onDialogListoOpen, onClose: onDialogListoClose } = useDisclosure();
  const { open: dialogBorradorOpen, onOpen: onDialogBorradorOpen, onClose: onDialogBorradorClose } = useDisclosure();
  const {
    open: dialogSalidaBorradorOpen,
    onOpen: onDialogSalidaBorradorOpen,
    onClose: onDialogSalidaBorradorClose,
  } = useDisclosure();
  const { open: dialogVaciarCarpetaOpen, onOpen: onDialogVaciarCarpetaOpen, onClose: onDialogVaciarCarpetaClose } =
    useDisclosure();
  const {
    open: dialogEliminarObraOpen,
    onOpen: onDialogEliminarObraOpen,
    onClose: onDialogEliminarObraClose,
  } = useDisclosure();
  const {
    open: dialogCrearMaestroOpen,
    onOpen: onDialogCrearMaestroOpen,
    onClose: onDialogCrearMaestroClose,
  } = useDisclosure();
  const {
    open: modalCrearCarpetaOpen,
    onOpen: onModalCrearCarpetaOpen,
    onClose: onModalCrearCarpetaClose,
  } = useDisclosure();
  const {
    open: dialogQrIncluirPwdOpen,
    onOpen: onDialogQrIncluirPwdOpen,
    onClose: onDialogQrIncluirPwdClose,
  } = useDisclosure();
  const { open: modalQrPwdOpen, onOpen: onModalQrPwdOpen, onClose: onModalQrPwdClose } = useDisclosure();
  const {
    open: modalYoutubeObraOpen,
    onOpen: onModalYoutubeObraOpen,
    onClose: onModalYoutubeObraClose,
  } = useDisclosure();
  const cancelListoRef = useRef(null);
  const cancelQrIncluirPwdRef = useRef(null);
  const cancelQrPwdRef = useRef(null);
  const cancelBorradorRef = useRef(null);
  const cancelSalidaBorradorRef = useRef(null);
  const salidaBorradorDestinoRef = useRef(null);
  const cancelVaciarCarpetaRef = useRef(null);
  const cancelEliminarObraRef = useRef(null);
  const [obraIdPendienteEliminar, setObraIdPendienteEliminar] = useState(null);
  const [deletingObra, setDeletingObra] = useState(false);
  const {
    open: dialogConfirmCatalogDeleteOpen,
    onOpen: onDialogConfirmCatalogDeleteOpen,
    onClose: onDialogConfirmCatalogDeleteClose,
  } = useDisclosure();
  const { open: isObrasExportOpen, onOpen: onObrasExportOpen, onClose: onObrasExportClose } = useDisclosure();
  const [obrasExportLoading, setObrasExportLoading] = useState(false);
  /** 'format' = PDF/Excel; 'pdf-tipo' = gestión o exhibición; 'pdf-orden' = orden del listado */
  const [obrasExportStep, setObrasExportStep] = useState('format');
  const [obrasExportPdfMode, setObrasExportPdfMode] = useState(null);
  const [obrasExportOrden, setObrasExportOrden] = useState('alfabetico');
  const cancelCatalogDeleteRef = useRef(null);
  const [pendingCatalogDelete, setPendingCatalogDelete] = useState(null);
  const [catalogDeleteLoading, setCatalogDeleteLoading] = useState(false);
  const cancelCrearMaestroRef = useRef(null);
  const [creandoMaestroAntesGuardar, setCreandoMaestroAntesGuardar] = useState(false);
  const [pendienteCrearMaestro, setPendienteCrearMaestro] = useState({ instrumentos: [], voces: [] });
  const [bulkActionStatus, setBulkActionStatus] = useState({ active: false, label: '' });
  const [sharePwd, setSharePwd] = useState('');
  const [sharePwd2, setSharePwd2] = useState('');
  const [sharePwdSaving, setSharePwdSaving] = useState(false);
  const [cambiarActual, setCambiarActual] = useState('');
  const [cambiarNueva, setCambiarNueva] = useState('');
  const [cambiarNueva2, setCambiarNueva2] = useState('');
  const [cambiarSaving, setCambiarSaving] = useState(false);
  const [verPwdAccount, setVerPwdAccount] = useState('');
  const [verPwdRevealed, setVerPwdRevealed] = useState('');
  const [verPwdLoading, setVerPwdLoading] = useState(false);

  const loadRepertorioShare = useCallback(async () => {
    try {
      setRepertorioShareLoading(true);
      const res = await bandasAPI.getRepertorioShare();
      setRepertorioShare({
        shareUrl: res.data?.shareUrl || '',
        hasPassword: !!res.data?.hasPassword,
        canRevealSharePassword: !!res.data?.canRevealSharePassword,
      });
    } catch (e) {
      console.error(e);
      setRepertorioShare({ shareUrl: '', hasPassword: false, canRevealSharePassword: false });
    } finally {
      setRepertorioShareLoading(false);
    }
  }, []);

  const loadMusicoEnlaces = useCallback(async () => {
    try {
      setMusicoEnlacesLoading(true);
      const res = await bandasAPI.getRepertorioShareMusicoEnlaces();
      setMusicoEnlaces({
        enabled: !!res.data?.enabled,
        musicos: Array.isArray(res.data?.musicos) ? res.data.musicos : [],
      });
    } catch (e) {
      console.error(e);
      setMusicoEnlaces({ enabled: false, musicos: [] });
    } finally {
      setMusicoEnlacesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('compartir') === 'incidencias') {
      setArchivoMusicaTabIndex(3);
      setCompartirSubTabIndex(1);
    }
  }, [searchParams]);

  useEffect(() => {
    if (archivoMusicaTabIndex === 3) {
      loadMusicoEnlaces();
      loadRepertorioShare();
    }
  }, [archivoMusicaTabIndex, loadMusicoEnlaces, loadRepertorioShare]);

  useEffect(() => {
    try {
      localStorage.setItem('cadencya-repertorio-obras-vista', obraVista);
    } catch {
      /* ignore */
    }
  }, [obraVista]);

  const loadList = useCallback(async () => {
    const res = await bandasAPI.listObrasPartituras();
    setObras(res.data || []);
  }, []);

  const loadPaginasBiblioteca = useCallback(async () => {
    try {
      setPaginasBibliotecaLoading(true);
      const res = await bandasAPI.listObrasPartiturasPaginas();
      const rows = Array.isArray(res.data) ? res.data : [];
      setPaginasBiblioteca(
        rows.map((row) => ({
          id: row.id,
          orden: row.orden,
          imagenUrl: row.imagenUrl,
          rotacion: row.rotacion,
          instrumentosVoces: row.instrumentosVoces,
          obraId: row.obra?.id,
          obraTitulo: row.obra?.titulo || 'Sin título',
          obraAutor: row.obra?.autor,
          obraNotasDirector: row.obra?.notasDirector,
          obraEstado: row.obra?.estado,
          obraGenero: row.obra?.genero,
          obraCarpetas: row.obra?.carpetasObra || [],
        })),
      );
    } catch (e) {
      console.error(e);
      setPaginasBiblioteca([]);
      toaster.create({
        title: 'No se pudieron cargar las páginas',
        type: 'error',
        duration: 4000,
        closable: true,
      });
    } finally {
      setPaginasBibliotecaLoading(false);
    }
  }, []);

  const loadCarpetas = useCallback(async () => {
    try {
      const res = await bandasAPI.listPartiturasCarpetas();
      setCarpetasCatalog(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setCarpetasCatalog([]);
    }
  }, []);

  // Snapshot de las páginas tal como están en el servidor (campos de etiquetado).
  // Se actualiza cada vez que el servidor es la fuente de verdad (carga o guardado total).
  const serverPaginasSnapshot = useRef(null);
  /** Metadatos de obra editados en el formulario y aún no guardados con «Guardar obra». */
  const obraMetaDirtyRef = useRef(false);

  const snapshotPaginasKey = (paginas) =>
    JSON.stringify(
      (paginas || []).map((p) => ({
        id: p.id,
        iv: p.instrumentosVoces ?? [],
        nd: p.notasDirector ?? null,
        to: p.tituloObra ?? null,
        au: p.autor ?? null,
        ocr: p.ocrTexto ?? null,
      })),
    );

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      serverPaginasSnapshot.current = null;
      return;
    }
    const res = await bandasAPI.getObraPartitura(id);
    const incoming = res.data;
    setDetail((prev) => {
      let next = incoming;
      if (prev && prev.id === incoming.id && obraMetaDirtyRef.current) {
        // Solo fusionamos el formulario principal; carpetas y páginas vienen del servidor.
        next = {
          ...incoming,
          titulo: prev.titulo,
          autor: prev.autor,
          notasDirector: prev.notasDirector,
          generoId: prev.generoId,
          genero: prev.genero,
        };
      }
      serverPaginasSnapshot.current = snapshotPaginasKey(next?.paginas);
      return next;
    });
  }, []);

  useEffect(() => {
    obraMetaDirtyRef.current = false;
    setObraMetaDirty(false);
  }, [selectedId]);

  useEffect(() => {
    const pages = detail?.paginas || [];
    if (!pages.length) {
      setSelectedPageId(null);
      return;
    }
    setSelectedPageId((prev) => (prev && pages.some((p) => p.id === prev) ? prev : pages[0].id));
  }, [detail]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadList();
        await loadCarpetas();
        await loadRepertorioShare();
      } catch (e) {
        console.error(e);
        toaster.create({ title: 'Error al cargar obras', type: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [loadList, loadCarpetas, loadRepertorioShare]);

  useEffect(() => {
    if (!selectedId) return;
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    setAmbitoPagina('individual');
    setRangoOrdenesTexto('');
  }, [selectedId, profile?.tipoGrupo]);

  useEffect(() => {
    setAmbitoPagina('individual');
  }, [selectedPageId]);

  useEffect(() => {
    if (archivoMusicaTabIndex === 1) loadCarpetas();
  }, [archivoMusicaTabIndex, loadCarpetas]);

  const loadInstrumentosPersonalizados = useCallback(async () => {
    try {
      const res = await instrumentosAPI.getPersonalizados();
      setInstrumentosPersonalizados(asInstrumentosPersonalizadosList(res.data));
    } catch (e) {
      console.error(e);
      setInstrumentosPersonalizados([]);
    }
  }, []);

  useEffect(() => {
    if (archivoMusicaTabIndex === 2) loadInstrumentosPersonalizados();
  }, [archivoMusicaTabIndex, loadInstrumentosPersonalizados]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = profile?.tipoGrupo ? { tipoGrupo: profile.tipoGrupo } : {};
        const [resAll, resPers] = await Promise.all([
          instrumentosAPI.getAll(params),
          instrumentosAPI.getPersonalizados(),
        ]);
        if (!cancelled) {
          setInstrumentCatalog(Array.isArray(resAll.data) ? resAll.data : []);
          setInstrumentosPersonalizados(asInstrumentosPersonalizadosList(resPers.data));
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setInstrumentCatalog([]);
          setInstrumentosPersonalizados([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile?.tipoGrupo]);

  const loadVocesCatalog = useCallback(async () => {
    try {
      const res = await bandasAPI.listPartiturasVoces();
      setVocesCatalog(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setVocesCatalog([]);
    }
  }, []);

  useEffect(() => {
    loadVocesCatalog();
  }, [loadVocesCatalog]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await bandasAPI.listObraPartituraGeneros();
        if (!cancelled) setGenerosCatalog(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        if (!cancelled) setGenerosCatalog([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const nombreVozPorEtiqueta = useCallback((etiqueta) => {
    if (!etiqueta) return '';
    const row = vocesCatalog.find(
      (v) => (v.etiqueta || etiquetaSlugDesdeNombre(v.nombre)) === etiqueta,
    );
    return row?.nombre || '';
  }, [vocesCatalog]);

  const lineaVoces = (slugs) => {
    if (!Array.isArray(slugs) || !slugs.length) return '';
    return slugs.map((s) => nombreVozPorEtiqueta(s) || s).join(', ');
  };

  const slugVozFila = (v) => v.etiqueta || etiquetaSlugDesdeNombre(v.nombre);

  const vocesPagina = (p) => {
    if (!p) return [];
    if (Array.isArray(p.vozEtiquetas)) return [...p.vozEtiquetas];
    if (p.vozEtiqueta) return [p.vozEtiqueta];
    return [];
  };

  const resumenPaginasEtiquetado = useMemo(() => {
    const list = detail?.paginas || [];
    let etiquetadas = 0;
    for (const p of list) {
      const tieneInstrumento = Array.isArray(p.instrumentosVoces) && p.instrumentosVoces.length > 0;
      const notas = (p.notasDirector || '').trim();
      if (tieneInstrumento || notas) etiquetadas += 1;
    }
    return { total: list.length, etiquetadas };
  }, [detail?.paginas]);

  useEffect(() => {
    if (modalEtiquetadoPaginasOpen && resumenPaginasEtiquetado.total === 0) {
      onModalEtiquetadoPaginasClose();
    }
  }, [modalEtiquetadoPaginasOpen, resumenPaginasEtiquetado.total, onModalEtiquetadoPaginasClose]);

  useEffect(() => {
    if (!selectedId && modalEtiquetadoPaginasOpen) onModalEtiquetadoPaginasClose();
  }, [selectedId, modalEtiquetadoPaginasOpen, onModalEtiquetadoPaginasClose]);

  const obraPortada = (o) => (Array.isArray(o?.paginas) && o.paginas[0] ? o.paginas[0] : null);

  const obraCarpetaIds = (o) =>
    (o?.carpetasObra || []).map((x) => x.carpeta?.id).filter(Boolean);

  const idsCarpetasDesdeDetail = (d) =>
    !d ? [] : (d.carpetasObra || []).map((x) => x.carpeta?.id).filter(Boolean);

  const filteredObras = useMemo(() => {
    const q = obraSearch.trim().toLowerCase();
    return obras.filter((o) => {
      if (obraGeneroFilterId && o.generoId !== obraGeneroFilterId) return false;
      if (obraEstadoFilter && o.estado !== obraEstadoFilter) return false;
      if (carpetaFiltroId && !obraCarpetaIds(o).includes(carpetaFiltroId)) return false;
      if (!q) return true;
      const hay = (s) => (s || '').toLowerCase().includes(q);
      return hay(o.titulo) || hay(o.autor) || hay(o.notasDirector);
    });
  }, [obras, obraSearch, obraGeneroFilterId, obraEstadoFilter, carpetaFiltroId]);

  const buildObrasExportSubtitle = useCallback((orden = 'alfabetico') => {
    const parts = [
      `${obras.length} obra${obras.length === 1 ? '' : 's'}`,
      orden === 'genero' ? 'Orden: género' : 'Orden: alfabético',
      new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }),
    ];
    return parts.join(' · ');
  }, [obras.length]);

  const closeObrasExportModal = useCallback(() => {
    if (obrasExportLoading) return;
    setObrasExportStep('format');
    setObrasExportPdfMode(null);
    setObrasExportOrden('alfabetico');
    onObrasExportClose();
  }, [obrasExportLoading, onObrasExportClose]);

  const handleExportObrasExcel = useCallback(async () => {
    if (!obras.length) {
      toaster.create({ title: 'No hay obras para exportar', type: 'info' });
      return;
    }
    try {
      setObrasExportLoading(true);
      const bandName = profile?.nombre || 'Mi banda';
      const subtitle = buildObrasExportSubtitle();
      await downloadObrasListadoExcel({ obras, bandName, subtitle });
      toaster.create({ title: 'Listado descargado', type: 'success' });
      closeObrasExportModal();
    } catch (e) {
      toaster.create({
        title: 'No se pudo generar el archivo',
        description: e?.message || 'Inténtalo de nuevo.',
        type: 'error',
        duration: 6000,
      });
    } finally {
      setObrasExportLoading(false);
    }
  }, [obras, profile?.nombre, buildObrasExportSubtitle, closeObrasExportModal]);

  const handleExportObrasPdf = useCallback(
    async (pdfMode, orden = obrasExportOrden) => {
      if (!obras.length) {
        toaster.create({ title: 'No hay obras para exportar', type: 'info' });
        return;
      }
      try {
        setObrasExportLoading(true);
        const bandName = profile?.nombre || 'Mi banda';
        const subtitle = buildObrasExportSubtitle(orden);
        if (pdfMode === 'exhibicion') {
          await downloadObrasListadoPdfExhibicion({
            obras,
            bandName,
            bandLogoUrl: profile?.avatar || undefined,
            subtitle,
            orden,
          });
        } else {
          await downloadObrasListadoPdfGestion({ obras, bandName, subtitle, orden });
        }
        toaster.create({ title: 'Listado descargado', type: 'success' });
        closeObrasExportModal();
      } catch (e) {
        toaster.create({
          title: 'No se pudo generar el archivo',
          description: e?.message || 'Inténtalo de nuevo.',
          type: 'error',
          duration: 6000,
        });
      } finally {
        setObrasExportLoading(false);
      }
    },
    [obras, profile?.nombre, profile?.avatar, obrasExportOrden, buildObrasExportSubtitle, closeObrasExportModal],
  );

  const filteredCarpetas = useMemo(() => {
    const q = (carpetasSearch || '').trim().toLowerCase();
    const base = (carpetasCatalog || []).filter((c) => {
      if (!q) return true;
      const hay = (s) => (s || '').toLowerCase().includes(q);
      return hay(c.nombre) || hay(c.descripcion);
    });
    const obrasCount = (c) => (c?.obras || []).length;
    const byNombre = (a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' });
    if (carpetasSort === 'nombre_desc') return [...base].sort((a, b) => byNombre(b, a));
    if (carpetasSort === 'obras_desc') return [...base].sort((a, b) => obrasCount(b) - obrasCount(a) || byNombre(a, b));
    if (carpetasSort === 'obras_asc') return [...base].sort((a, b) => obrasCount(a) - obrasCount(b) || byNombre(a, b));
    return [...base].sort(byNombre);
  }, [carpetasCatalog, carpetasSearch, carpetasSort]);

  useEffect(() => {
    setCarpetasPage(1);
  }, [carpetasSearch, carpetasSort, carpetasPageSize]);

  const carpetasPagination = useMemo(() => {
    const total = filteredCarpetas.length;
    const pageSize = Math.max(1, Number(carpetasPageSize) || 10);
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(Math.max(1, Number(carpetasPage) || 1), totalPages);
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = total === 0 ? 0 : Math.min(total, page * pageSize);
    const sliceStart = (page - 1) * pageSize;
    const sliceEnd = sliceStart + pageSize;
    return { total, pageSize, totalPages, page, start, end, sliceStart, sliceEnd };
  }, [filteredCarpetas.length, carpetasPage, carpetasPageSize]);

  const pagedCarpetas = useMemo(
    () => filteredCarpetas.slice(carpetasPagination.sliceStart, carpetasPagination.sliceEnd),
    [filteredCarpetas, carpetasPagination.sliceStart, carpetasPagination.sliceEnd],
  );

  const selectedCarpeta = useMemo(
    () => (selectedCarpetaId ? carpetasCatalog.find((c) => c.id === selectedCarpetaId) ?? null : null),
    [selectedCarpetaId, carpetasCatalog],
  );

  useEffect(() => {
    if (bibliotecaModo === 'paginas') loadPaginasBiblioteca();
  }, [bibliotecaModo, loadPaginasBiblioteca]);

  const filteredPaginas = useMemo(() => {
    const obraIds = new Set(filteredObras.map((o) => o.id));
    let list = paginasBiblioteca.filter((p) => p.obraId && obraIds.has(p.obraId));
    if (paginaInstrumentoFilter) {
      list = list.filter((p) =>
        paginaInstrumentoCoincideFiltro(p.instrumentosVoces, paginaInstrumentoFilter),
      );
    }
    return list;
  }, [paginasBiblioteca, filteredObras, paginaInstrumentoFilter]);

  const paginaInstrumentoLabels = useCallback((p) => {
    const iv = Array.isArray(p?.instrumentosVoces) ? p.instrumentosVoces : [];
    return iv.map((x) => x?.nombre || x?.slug).filter(Boolean);
  }, []);

  useEffect(() => {
    setObraPage(1);
  }, [obraSearch, obraGeneroFilterId, obraEstadoFilter, carpetaFiltroId, obraPageSize, bibliotecaModo, paginaInstrumentoFilter]);

  const obraPagination = useMemo(() => {
    const total = filteredObras.length;
    const pageSize = Math.max(1, Number(obraPageSize) || 25);
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(Math.max(1, Number(obraPage) || 1), totalPages);
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = total === 0 ? 0 : Math.min(total, page * pageSize);
    const sliceStart = (page - 1) * pageSize;
    const sliceEnd = sliceStart + pageSize;
    return { total, pageSize, totalPages, page, start, end, sliceStart, sliceEnd };
  }, [filteredObras.length, obraPage, obraPageSize]);

  const pagedObras = useMemo(
    () => filteredObras.slice(obraPagination.sliceStart, obraPagination.sliceEnd),
    [filteredObras, obraPagination.sliceStart, obraPagination.sliceEnd],
  );

  const paginasPagination = useMemo(() => {
    const total = filteredPaginas.length;
    const pageSize = Math.max(1, Number(obraPageSize) || 25);
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(Math.max(1, Number(obraPage) || 1), totalPages);
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = total === 0 ? 0 : Math.min(total, page * pageSize);
    const sliceStart = (page - 1) * pageSize;
    const sliceEnd = sliceStart + pageSize;
    return { total, pageSize, totalPages, page, start, end, sliceStart, sliceEnd };
  }, [filteredPaginas.length, obraPage, obraPageSize]);

  const pagedPaginas = useMemo(
    () => filteredPaginas.slice(paginasPagination.sliceStart, paginasPagination.sliceEnd),
    [filteredPaginas, paginasPagination.sliceStart, paginasPagination.sliceEnd],
  );

  const bibliotecaPagination =
    bibliotecaModo === 'paginas' ? paginasPagination : obraPagination;

  /** Orden de obras para el PDF: si hay filtro de carpeta, orden de programa de esa carpeta; si no, orden del listado filtrado. */
  const partichellaObraIdsOrdenados = useMemo(() => {
    if (!carpetaFiltroId) return filteredObras.map((o) => o.id);
    return [...filteredObras]
      .map((o) => {
        const co = (o.carpetasObra || []).find((x) => x.carpeta?.id === carpetaFiltroId);
        return { id: o.id, orden: co !== undefined ? co.orden : 999999 };
      })
      .sort((a, b) => a.orden - b.orden)
      .map((x) => x.id);
  }, [filteredObras, carpetaFiltroId]);

  const partichellaObraIdsKey = useMemo(() => partichellaObraIdsOrdenados.join(','), [partichellaObraIdsOrdenados]);

  useEffect(() => {
    setPartichellasVocesSel([]);
  }, [partichellasInstrumentSlug]);

  useEffect(() => {
    if (!modalPartichellasAbierto) {
      setPartichellasVocesPreview([]);
      setPartichellasPreviewLoading(false);
      return undefined;
    }
    if (!partichellasInstrumentSlug || !partichellaObraIdsKey) {
      setPartichellasVocesPreview([]);
      return undefined;
    }
    if (partichellasInstrumentSlug === PARTICHELLAS_INSTR_TODAS) {
      setPartichellasVocesPreview([]);
      setPartichellasPreviewLoading(false);
      return undefined;
    }
    const ids = partichellaObraIdsKey.split(',').filter(Boolean);
    if (!ids.length) {
      setPartichellasVocesPreview([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setPartichellasPreviewLoading(true);
        const res = await bandasAPI.previewPartichellasVoces({
          obraIds: ids,
          instrumentoEtiqueta: partichellasInstrumentSlug,
        });
        if (!cancelled) setPartichellasVocesPreview(Array.isArray(res.data?.voces) ? res.data.voces : []);
      } catch (e) {
        if (!cancelled) {
          setPartichellasVocesPreview([]);
          toaster.create({
            title: 'No se pudieron obtener las voces',
            description: e.response?.data?.message,
            type: 'error',
            duration: 5000,
            closable: true,
          });
        }
      } finally {
        if (!cancelled) setPartichellasPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalPartichellasAbierto, partichellasInstrumentSlug, partichellaObraIdsKey]);

  const filteredObrasCarpetaDrawer = useMemo(() => {
    const q = carpetaDrawerSearch.trim().toLowerCase();
    return obras.filter((o) => {
      if (carpetaDrawerGeneroId && o.generoId !== carpetaDrawerGeneroId) return false;
      if (carpetaDrawerEstado && o.estado !== carpetaDrawerEstado) return false;
      if (!q) return true;
      const hay = (s) => (s || '').toLowerCase().includes(q);
      return hay(o.titulo) || hay(o.autor) || hay(o.notasDirector);
    });
  }, [obras, carpetaDrawerSearch, carpetaDrawerGeneroId, carpetaDrawerEstado]);

  const cerrarGestionObrasCarpeta = useCallback(() => {
    setDrawerCarpetaObras(null);
    setCarpetaDrawerOrdenIds([]);
  }, []);

  const goToCarpetasList = useCallback(() => {
    setSelectedCarpetaId(null);
    setCarpetaViewMode('ficha');
    setRenombrarCarpetaTexto('');
    setRenombrarCarpetaDescripcion('');
  }, []);

  const openCarpetaFicha = useCallback((c) => {
    setCarpetaViewMode('ficha');
    setSelectedCarpetaId(c.id);
  }, []);

  const openCarpetaEdit = useCallback(() => {
    if (!selectedCarpeta) return;
    setRenombrarCarpetaTexto(selectedCarpeta.nombre || '');
    setRenombrarCarpetaDescripcion(selectedCarpeta.descripcion ?? '');
    setCarpetaViewMode('edit');
  }, [selectedCarpeta]);

  const abrirDrawerObrasCarpeta = useCallback((c) => {
    const rows = [...(c.obras || [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    setCarpetaDrawerOrdenIds(rows.map((x) => x.obra?.id).filter(Boolean));
    setCarpetaDrawerSearch('');
    setCarpetaDrawerGeneroId('');
    setCarpetaDrawerEstado('');
    setDrawerCarpetaObras(c);
  }, []);

  const toggleObraEnCarpetaDrawer = useCallback((obraId, checked) => {
    setCarpetaDrawerOrdenIds((prev) => {
      if (checked) {
        if (prev.includes(obraId)) return prev;
        return [...prev, obraId];
      }
      return prev.filter((x) => x !== obraId);
    });
  }, []);

  const onDragEndCarpetaPrograma = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCarpetaDrawerOrdenIds((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const ejecutarSalidaObra = useCallback((destino) => {
    if (!destino) return;
    if (destino.type === 'list') {
      setSelectedId(null);
      setDetail(null);
      setSelectedPageId(null);
      setObraViewMode('ficha');
      return;
    }
    if (destino.type === 'ficha') {
      setObraViewMode('ficha');
      return;
    }
    if (destino.type === 'obra') {
      setObraViewMode(destino.mode || 'ficha');
      setSelectedId(destino.id);
    }
  }, []);

  const intentarSalirObraBorrador = useCallback(
    (destino) => {
      if (selectedId && detail?.estado !== 'LISTO') {
        salidaBorradorDestinoRef.current = destino;
        onDialogSalidaBorradorOpen();
        return;
      }
      ejecutarSalidaObra(destino);
    },
    [selectedId, detail?.estado, ejecutarSalidaObra, onDialogSalidaBorradorOpen],
  );

  const confirmarSalidaObraBorrador = useCallback(() => {
    const destino = salidaBorradorDestinoRef.current;
    salidaBorradorDestinoRef.current = null;
    onDialogSalidaBorradorClose();
    ejecutarSalidaObra(destino);
  }, [ejecutarSalidaObra, onDialogSalidaBorradorClose]);

  const goToBrowseList = useCallback(() => {
    intentarSalirObraBorrador({ type: 'list' });
  }, [intentarSalirObraBorrador]);

  const openObraFicha = useCallback(
    (id) => {
      if (selectedId && selectedId !== id) {
        intentarSalirObraBorrador({ type: 'obra', id, mode: 'ficha' });
        return;
      }
      setObraViewMode('ficha');
      setSelectedId(id);
    },
    [selectedId, intentarSalirObraBorrador],
  );

  const openObraEdit = useCallback(
    (id) => {
      if (selectedId && selectedId !== id) {
        intentarSalirObraBorrador({ type: 'obra', id, mode: 'edit' });
        return;
      }
      setObraViewMode('edit');
      setSelectedId(id);
    },
    [selectedId, intentarSalirObraBorrador],
  );

  const abrirEtiquetadoObra = useCallback(() => {
    if (!resumenPaginasEtiquetado.total) {
      toaster.create({
        title: 'Aún no hay páginas',
        description: 'Usa «Añadir páginas» en la ficha o en el visor; luego podrás etiquetarlas.',
        type: 'info',
        duration: 4000,
      });
      return;
    }
    onModalEtiquetadoPaginasOpen();
  }, [resumenPaginasEtiquetado.total, onModalEtiquetadoPaginasOpen]);

  const abrirVisorPagina = useCallback((pageId, pagesList = null) => {
    setVisorNavegacionPaginas(pagesList && pagesList.length ? pagesList : []);
    setSelectedPageId(pageId);
    setVisorZoom(1);
    setVisorImgNatural({ w: 0, h: 0 });
    // Controles visibles al abrir; en móvil un toque los oculta / muestra.
    setVisorChromeVisible(true);
    setVisorPartituraAbierto(true);
  }, []);

  const abrirPaginaBiblioteca = useCallback(
    (page) => {
      abrirVisorPagina(page.id, filteredPaginas);
    },
    [abrirVisorPagina, filteredPaginas],
  );

  const descargarPaginaObra = useCallback(
    async (page) => {
      if (!page?.imagenUrl) return;
      try {
        setDescargandoPaginaId(page.id);
        await downloadPartituraPaginaImage({
          imagenUrl: page.imagenUrl,
          rotacion: page.rotacion || 0,
          obraTitulo: detail?.titulo || page.obraTitulo,
          orden: page.orden,
        });
      } catch (e) {
        toaster.create({
          title: 'No se pudo descargar la página',
          description: e?.message || 'Inténtalo de nuevo.',
          type: 'error',
          duration: 5000,
        });
      } finally {
        setDescargandoPaginaId(null);
      }
    },
    [detail?.titulo],
  );

  const descargarObraCompleta = useCallback(async () => {
    if (!selectedId || !detail) return;
    if (!detail.paginas?.length) {
      toaster.create({
        title: 'Sin páginas',
        description: 'Esta obra no tiene páginas guardadas para descargar.',
        type: 'info',
      });
      return;
    }
    try {
      setDescargandoObraCompleta(true);
      const res = await bandasAPI.downloadPartichellasPdf({
        obraIds: [selectedId],
        todasLasPaginas: true,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      downloadBlob(
        blob,
        buildPartituraDownloadFilename({
          primary: detail.titulo || 'obra',
          todas: true,
          ext: 'pdf',
        }),
      );
      toaster.create({ title: 'PDF de la obra descargado', type: 'success' });
    } catch (e) {
      let msg = e.response?.data?.message;
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          const j = JSON.parse(t);
          msg = j.message || t;
        } catch {
          /* ignore */
        }
      }
      toaster.create({
        title: 'No se pudo generar el PDF',
        description: msg || e?.message || 'Inténtalo de nuevo.',
        type: 'error',
        duration: 6000,
      });
    } finally {
      setDescargandoObraCompleta(false);
    }
  }, [selectedId, detail]);

  // Al entrar al detalle (desde una lista con scroll), forzamos scroll arriba para UX consistente.
  useEffect(() => {
    if (!selectedId) return;
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [selectedId]);

  const handleAddVoz = async () => {
    const n = nuevaVozNombre.trim();
    if (!n) {
      toaster.create({ title: 'Escribe un nombre de voz', type: 'warning' });
      return;
    }
    try {
      setVocesMutating(true);
      const res = await bandasAPI.createPartituraVoz({ nombre: n });
      setVocesCatalog(Array.isArray(res.data) ? res.data : []);
      setNuevaVozNombre('');
      toaster.create({ title: 'Voz añadida', type: 'success' });
    } catch (e) {
      toaster.create({
        title: 'No se pudo añadir',
        description: e.response?.data?.message,
        type: 'error',
      });
    } finally {
      setVocesMutating(false);
    }
  };

  /** Desde el visor de etiquetado: crea voz en el maestro y refresca el catálogo. */
  const crearVozMaestroDesdeEtiquetado = useCallback(
    async (nombre) => {
      const n = String(nombre || '').trim();
      if (!n) {
        toaster.create({ title: 'Escribe un nombre de voz', type: 'warning' });
        return false;
      }
      try {
        setVocesMutating(true);
        const res = await bandasAPI.createPartituraVoz({ nombre: n });
        setVocesCatalog(Array.isArray(res.data) ? res.data : []);
        toaster.create({ title: 'Voz añadida al maestro', type: 'success' });
        return true;
      } catch (e) {
        toaster.create({
          title: 'No se pudo añadir la voz',
          description: e.response?.data?.message,
          type: 'error',
        });
        return false;
      } finally {
        setVocesMutating(false);
      }
    },
    [],
  );

  /** Desde el visor de etiquetado: crea instrumento personalizado (familia OTRO) y refresca catálogos. */
  const crearInstrumentoMaestroDesdeEtiquetado = useCallback(
    async (nombre) => {
      const n = String(nombre || '').trim();
      if (!n) {
        toaster.create({ title: 'Escribe el nombre del instrumento', type: 'warning' });
        return false;
      }
      try {
        setInstPersMutating(true);
        const createRes = await instrumentosAPI.createPersonalizado({ nombre: n, familia: 'OTRO' });
        const created = createRes?.data;
        const listRes = await instrumentosAPI.getPersonalizados();
        let list = asInstrumentosPersonalizadosList(listRes.data);
        if (created?.id && !list.some((x) => x.id === created.id)) {
          list = [...list, created].sort((a, b) =>
            (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }),
          );
        }
        setInstrumentosPersonalizados(list);
        const res = await instrumentosAPI.getAll(profile?.tipoGrupo ? { tipoGrupo: profile.tipoGrupo } : {});
        setInstrumentCatalog(Array.isArray(res.data) ? res.data : []);
        toaster.create({ title: 'Instrumento añadido al maestro', type: 'success' });
        return true;
      } catch (e) {
        const msg = e?.response?.data?.message;
        toaster.create({
          title: 'No se pudo crear el instrumento',
          description: Array.isArray(msg) ? msg[0] : msg || e.message,
          type: 'error',
        });
        return false;
      } finally {
        setInstPersMutating(false);
      }
    },
    [profile?.tipoGrupo],
  );

  const handleDeleteVoz = async (id, nombre) => {
    setPendingCatalogDelete({ type: 'voz', id, nombre: nombre || '' });
    onDialogConfirmCatalogDeleteOpen();
  };

  const confirmDeleteCatalogItem = useCallback(async () => {
    if (!pendingCatalogDelete?.id) return;
    try {
      setCatalogDeleteLoading(true);
      if (pendingCatalogDelete.type === 'voz') {
        setVocesMutating(true);
        await bandasAPI.deletePartituraVoz(pendingCatalogDelete.id);
        await loadVocesCatalog();
        toaster.create({ title: 'Voz eliminada', type: 'success' });
      } else if (pendingCatalogDelete.type === 'instrumento') {
        setInstPersMutating(true);
        await instrumentosAPI.deletePersonalizado(pendingCatalogDelete.id);
        const listRes = await instrumentosAPI.getPersonalizados();
        setInstrumentosPersonalizados(asInstrumentosPersonalizadosList(listRes.data));
        const res = await instrumentosAPI.getAll(profile?.tipoGrupo ? { tipoGrupo: profile.tipoGrupo } : {});
        setInstrumentCatalog(Array.isArray(res.data) ? res.data : []);
        toaster.create({ title: 'Instrumento eliminado', type: 'success' });
      } else if (pendingCatalogDelete.type === 'carpeta') {
        setCarpetasMutating(true);
        await bandasAPI.deletePartituraCarpeta(pendingCatalogDelete.id);
        if (carpetaFiltroId === pendingCatalogDelete.id) setCarpetaFiltroId('');
        if (selectedCarpetaId === pendingCatalogDelete.id) {
          setSelectedCarpetaId(null);
          setCarpetaViewMode('ficha');
        }
        await loadCarpetas();
        await loadList();
        if (selectedId) await loadDetail(selectedId);
        toaster.create({ title: 'Carpeta eliminada', type: 'success' });
      }
      setPendingCatalogDelete(null);
      onDialogConfirmCatalogDeleteClose();
    } catch {
      if (pendingCatalogDelete?.type === 'carpeta') {
        toaster.create({ title: 'Error al eliminar carpeta', type: 'error' });
      } else if (pendingCatalogDelete?.type === 'instrumento') {
        toaster.create({ title: 'No se pudo eliminar el instrumento', type: 'error' });
      } else {
        toaster.create({ title: 'Error al eliminar', type: 'error' });
      }
    } finally {
      setCatalogDeleteLoading(false);
      setVocesMutating(false);
      setInstPersMutating(false);
      setCarpetasMutating(false);
    }
  }, [
    pendingCatalogDelete,
    onDialogConfirmCatalogDeleteClose,
    loadVocesCatalog,
    profile?.tipoGrupo,
    carpetaFiltroId,
    loadCarpetas,
    loadList,
    selectedId,
    loadDetail,
  ]);

  const paginasOrdenadasEditor = useMemo(
    () => [...(detail?.paginas || [])].sort((a, b) => a.orden - b.orden),
    [detail?.paginas],
  );

  const paginasOrdenadasVisor = useMemo(() => {
    if (visorNavegacionPaginas.length > 0) return visorNavegacionPaginas;
    return paginasOrdenadasEditor;
  }, [visorNavegacionPaginas, paginasOrdenadasEditor]);

  const selectedPage = useMemo(
    () => paginasOrdenadasVisor.find((p) => p.id === selectedPageId) || null,
    [paginasOrdenadasVisor, selectedPageId],
  );

  // True si hay cambios en el visor de etiquetado que no se han guardado en el servidor.
  const isDirtyModal = useMemo(() => {
    if (!serverPaginasSnapshot.current || !detail?.paginas) return false;
    return serverPaginasSnapshot.current !== snapshotPaginasKey(detail.paginas);
  }, [detail?.paginas]);

  const clampVisorZoom = (z) => Math.max(0.35, Math.min(4, z));

  const cerrarVisorPartitura = useCallback(() => {
    setVisorPartituraAbierto(false);
    setVisorNavegacionPaginas([]);
    setVisorZoom(1);
    setVisorChromeVisible(true);
    setVisorImgNatural({ w: 0, h: 0 });
  }, []);

  const visorIrAnterior = useCallback(() => {
    const i = paginasOrdenadasVisor.findIndex((p) => p.id === selectedPageId);
    if (i > 0) {
      setSelectedPageId(paginasOrdenadasVisor[i - 1].id);
      setVisorZoom(1);
      setVisorImgNatural({ w: 0, h: 0 });
      return true;
    }
    const currentObraId =
      selectedId ||
      selectedPage?.obraId ||
      paginasOrdenadasVisor[i]?.obraId ||
      null;
    const list = filteredObras.length ? filteredObras : obras;
    const oi = list.findIndex((o) => o.id === currentObraId);
    if (oi > 0) {
      setVisorNavegacionPaginas([]);
      setVisorZoom(1);
      setVisorImgNatural({ w: 0, h: 0 });
      setObraViewMode('ficha');
      setSelectedId(list[oi - 1].id);
      return true;
    }
    return false;
  }, [
    paginasOrdenadasVisor,
    selectedPageId,
    filteredObras,
    obras,
    selectedId,
    selectedPage?.obraId,
  ]);

  const visorIrSiguiente = useCallback(() => {
    const i = paginasOrdenadasVisor.findIndex((p) => p.id === selectedPageId);
    if (i >= 0 && i < paginasOrdenadasVisor.length - 1) {
      setSelectedPageId(paginasOrdenadasVisor[i + 1].id);
      setVisorZoom(1);
      setVisorImgNatural({ w: 0, h: 0 });
      return true;
    }
    const currentObraId =
      selectedId ||
      selectedPage?.obraId ||
      paginasOrdenadasVisor[i]?.obraId ||
      null;
    const list = filteredObras.length ? filteredObras : obras;
    const oi = list.findIndex((o) => o.id === currentObraId);
    if (oi >= 0 && oi < list.length - 1) {
      setVisorNavegacionPaginas([]);
      setVisorZoom(1);
      setVisorImgNatural({ w: 0, h: 0 });
      setObraViewMode('ficha');
      setSelectedId(list[oi + 1].id);
      return true;
    }
    return false;
  }, [
    paginasOrdenadasVisor,
    selectedPageId,
    filteredObras,
    obras,
    selectedId,
    selectedPage?.obraId,
  ]);

  const idxVisorPartitura = paginasOrdenadasVisor.findIndex((p) => p.id === selectedPageId);
  const visorPuedeAnterior = (() => {
    if (idxVisorPartitura > 0) return true;
    const currentObraId =
      selectedId || selectedPage?.obraId || paginasOrdenadasVisor[idxVisorPartitura]?.obraId;
    const list = filteredObras.length ? filteredObras : obras;
    return list.findIndex((o) => o.id === currentObraId) > 0;
  })();
  const visorPuedeSiguiente = (() => {
    if (idxVisorPartitura >= 0 && idxVisorPartitura < paginasOrdenadasVisor.length - 1) return true;
    const currentObraId =
      selectedId || selectedPage?.obraId || paginasOrdenadasVisor[idxVisorPartitura]?.obraId;
    const list = filteredObras.length ? filteredObras : obras;
    const oi = list.findIndex((o) => o.id === currentObraId);
    return oi >= 0 && oi < list.length - 1;
  })();

  useEffect(() => {
    if (!visorPartituraAbierto) return undefined;
    // Solo al abrir (o al cambiar móvil/desktop): controles visibles; en móvil se togglean al tocar.
    setVisorChromeVisible(true);
    return undefined;
  }, [visorPartituraAbierto, isMobileVisor]);

  useEffect(() => {
    if (!visorPartituraAbierto) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        cerrarVisorPartitura();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        visorIrAnterior();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        visorIrSiguiente();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visorPartituraAbierto, cerrarVisorPartitura, visorIrAnterior, visorIrSiguiente]);

  useEffect(() => {
    if (!visorPartituraAbierto) return undefined;
    const el = visorViewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setVisorViewportSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [visorPartituraAbierto, selectedPageId]);

  const visorDisplaySize = useMemo(() => {
    const { w: nw, h: nh } = visorImgNatural;
    const { w: cw, h: ch } = visorViewportSize;
    if (!nw || !nh || !cw || !ch) return null;
    const rot = Number(selectedPage?.rotacion) || 0;
    const rad = ((rot % 360) * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(rad));
    const absSin = Math.abs(Math.sin(rad));
    const bw = nw * absCos + nh * absSin;
    const bh = nw * absSin + nh * absCos;
    const fit = Math.min(cw / bw, ch / bh);
    const safeFit = Number.isFinite(fit) && fit > 0 ? fit : 1;
    const scale = safeFit * visorZoom;
    return {
      w: nw * scale,
      h: nh * scale,
      layoutW: bw * scale,
      layoutH: bh * scale,
      fit: safeFit,
    };
  }, [visorImgNatural, visorViewportSize, visorZoom, selectedPage?.rotacion]);

  const handleAltaObrasCompletadas = useCallback(
    async () => {
      await loadList();
      await loadCarpetas();
      setSelectedId(null);
      setObraViewMode('ficha');
      obraMetaDirtyRef.current = false;
      setObraMetaDirty(false);
      toaster.create({
        title: 'Obras creadas en borrador',
        description:
          'Revisa título, páginas y etiquetas de cada obra. Cuando esté correcto, usa «Pasar a listo» en el editor.',
        type: 'info',
        duration: 8000,
      });
    },
    [loadList, loadCarpetas],
  );

  const persistAllPagesToServer = useCallback(async (obraDetail) => {
    if (!selectedId || !obraDetail?.paginas?.length) return;
    // Secuencial: evita condiciones de carrera en BD y asegura orden estable al guardar muchas páginas.
    const pages = [...obraDetail.paginas].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    for (const p of pages) {
      // eslint-disable-next-line no-await-in-loop
      await bandasAPI.updateObraPartituraPagina(selectedId, p.id, buildObraPartituraPaginaPatch(p));
    }
  }, [selectedId]);

  const detectarNuevosMaestrosEnEtiquetado = useCallback((obraDetail) => {
    const knownInstSlugs = new Set(
      [
        ...instrumentCatalog.map((i) => etiquetaSlugDesdeNombre(i.nombre)),
        ...instrumentosPersonalizados.map((i) => etiquetaSlugDesdeNombre(i.nombre)),
      ].filter(Boolean),
    );
    const knownInstBases = new Set(
      Array.from(knownInstSlugs).map((s) => String(s).replace(/_\d+$/, '')).filter(Boolean),
    );
    const knownNombreKeys = new Set(
      [
        ...instrumentCatalog.map((i) => String(i.nombre || '').trim().toLowerCase()),
        ...instrumentosPersonalizados.map((i) => String(i.nombre || '').trim().toLowerCase()),
      ].filter(Boolean),
    );
    const knownVozSlugs = new Set(vocesCatalog.map((v) => slugVozFila(v)).filter(Boolean));

    const nuevosInstrumentosMap = new Map();
    const nuevasVocesMap = new Map();
    const paginas = obraDetail?.paginas || [];
    for (const p of paginas) {
      const entries = Array.isArray(p.instrumentosVoces) ? p.instrumentosVoces : [];
      for (const entry of entries) {
        const instSlugRaw = typeof entry?.slug === 'string' ? entry.slug.trim() : '';
        const instSlug = instSlugRaw || '';
        const instBase = instSlug ? instSlug.replace(/_\d+$/, '') : '';
        const nombreRaw = String(entry?.nombre || '').trim();
        const nameNorm = etiquetaSlugDesdeNombre(nombreRaw) || '';
        const nameBase = nameNorm ? nameNorm.replace(/_\d+$/, '') : '';
        const nombreKey = nombreRaw.toLowerCase();

        const isKnown =
          (instSlug && knownInstSlugs.has(instSlug)) ||
          (instBase && knownInstBases.has(instBase)) ||
          (nameNorm && knownInstSlugs.has(nameNorm)) ||
          (nameBase && knownInstBases.has(nameBase)) ||
          (nombreKey && knownNombreKeys.has(nombreKey));

        if (!isKnown) {
          // Clave estable por nombre normalizado (evita Tambor / tambor como altas distintas).
          const key = nameNorm || nameBase || instBase || instSlug;
          if (key && !nuevosInstrumentosMap.has(key)) {
            const nombre = nombreRaw || humanizeSlugLabel(key);
            nuevosInstrumentosMap.set(key, { slug: key, nombre });
          }
        }
        const voces = Array.isArray(entry?.voces) ? entry.voces : [];
        for (const vozSlugRaw of voces) {
          const vozSlug = typeof vozSlugRaw === 'string' ? vozSlugRaw.trim() : '';
          if (!vozSlug || knownVozSlugs.has(vozSlug) || nuevasVocesMap.has(vozSlug)) continue;
          nuevasVocesMap.set(vozSlug, { slug: vozSlug, nombre: humanizeSlugLabel(vozSlug) || vozSlug });
        }
      }
    }
    return {
      instrumentos: Array.from(nuevosInstrumentosMap.values()),
      voces: Array.from(nuevasVocesMap.values()),
    };
  }, [instrumentCatalog, instrumentosPersonalizados, vocesCatalog, slugVozFila]);

  const crearMaestrosPendientes = useCallback(async (faltantes) => {
    const knownInstKeys = new Set(
      [
        ...instrumentCatalog.map((i) => etiquetaSlugDesdeNombre(i.nombre)),
        ...instrumentosPersonalizados.map((i) => etiquetaSlugDesdeNombre(i.nombre)),
      ].filter(Boolean),
    );
    const seenCreate = new Set();

    for (const inst of faltantes.instrumentos || []) {
      const nombre = String(inst?.nombre || '').trim();
      if (!nombre) continue;
      const key = etiquetaSlugDesdeNombre(nombre);
      if (!key || knownInstKeys.has(key) || seenCreate.has(key)) continue;
      seenCreate.add(key);
      try {
        await instrumentosAPI.createPersonalizado({ nombre, familia: 'OTRO' });
        knownInstKeys.add(key);
      } catch (e) {
        // 409: ya en catálogo general o personalizado — no bloquear el resto.
        if (e?.response?.status !== 409) {
          console.warn('No se pudo crear instrumento personalizado', nombre, e?.response?.data || e);
        }
      }
    }
    const knownVoz = new Set(vocesCatalog.map((v) => slugVozFila(v)).filter(Boolean));
    for (const voz of faltantes.voces || []) {
      const nombre = String(voz?.nombre || '').trim();
      if (!nombre) continue;
      const key = etiquetaSlugDesdeNombre(nombre);
      if (!key || knownVoz.has(key)) continue;
      knownVoz.add(key);
      try {
        await bandasAPI.createPartituraVoz({ nombre });
      } catch (e) {
        if (e?.response?.status !== 409) {
          console.warn('No se pudo crear voz personalizada', nombre, e?.response?.data || e);
        }
      }
    }
    const params = profile?.tipoGrupo ? { tipoGrupo: profile.tipoGrupo } : {};
    const [resAll, resPers, resVoces] = await Promise.all([
      instrumentosAPI.getAll(params),
      instrumentosAPI.getPersonalizados(),
      bandasAPI.listPartiturasVoces(),
    ]);
    setInstrumentCatalog(Array.isArray(resAll.data) ? resAll.data : []);
    setInstrumentosPersonalizados(asInstrumentosPersonalizadosList(resPers.data));
    setVocesCatalog(Array.isArray(resVoces.data) ? resVoces.data : []);
  }, [profile?.tipoGrupo, instrumentCatalog, instrumentosPersonalizados, vocesCatalog, slugVozFila]);

  const saveAllPagesEtiquetas = useCallback(async (options = {}) => {
    if (!selectedId || !detail) return;
    const skipNuevosCheck = options?.skipNuevosCheck === true;
    if (!skipNuevosCheck) {
      const faltantes = detectarNuevosMaestrosEnEtiquetado(detail);
      if (faltantes.instrumentos.length || faltantes.voces.length) {
        setPendienteCrearMaestro(faltantes);
        onDialogCrearMaestroOpen();
        return;
      }
    }
    try {
      setBulkActionStatus({ active: true, label: 'Guardando todas las páginas…' });
      setSaving(true);
      await persistAllPagesToServer(detail);
      // Actualiza el snapshot para que isDirtyModal vuelva a false sin recargar el servidor
      serverPaginasSnapshot.current = snapshotPaginasKey(detail.paginas);
      await loadDetail(selectedId);
      toaster.create({
        title: 'Todas las páginas guardadas',
        description: 'El etiquetado de cada página ya está en el servidor.',
        type: 'success',
        duration: 5000,
        closable: true,
      });
    } catch (e) {
      toaster.create({
        title: 'Error al guardar páginas',
        description: e.response?.data?.message || e.message,
        type: 'error',
        duration: 6000,
        closable: true,
      });
    } finally {
      setBulkActionStatus({ active: false, label: '' });
      setSaving(false);
    }
  }, [selectedId, detail, detectarNuevosMaestrosEnEtiquetado, onDialogCrearMaestroOpen, persistAllPagesToServer, loadDetail]);

  const confirmarCrearMaestrosYGuardar = useCallback(async () => {
    try {
      setBulkActionStatus({ active: true, label: 'Creando instrumentos y voces nuevas…' });
      setCreandoMaestroAntesGuardar(true);
      await crearMaestrosPendientes(pendienteCrearMaestro);
      onDialogCrearMaestroClose();
      await saveAllPagesEtiquetas({ skipNuevosCheck: true });
      setPendienteCrearMaestro({ instrumentos: [], voces: [] });
      toaster.create({
        title: 'Maestros actualizados',
        description: 'Se han creado instrumentos/voces nuevos antes de guardar el etiquetado.',
        type: 'success',
        duration: 5000,
        closable: true,
      });
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudieron crear los nuevos registros',
        description: Array.isArray(msg) ? msg[0] : msg || e.message,
        type: 'error',
        duration: 6000,
        closable: true,
      });
    } finally {
      setCreandoMaestroAntesGuardar(false);
      setBulkActionStatus({ active: false, label: '' });
    }
  }, [crearMaestrosPendientes, pendienteCrearMaestro, onDialogCrearMaestroClose, saveAllPagesEtiquetas]);

  const saveObraMeta = async () => {
    if (!selectedId || !detail) return;
    try {
      setSaving(true);
      await bandasAPI.updateObraPartitura(selectedId, {
        titulo: detail.titulo,
        autor: detail.autor,
        notasDirector: detail.notasDirector,
        youtubeUrl: detail.youtubeUrl?.trim() || null,
        generoId: detail.generoId || null,
        carpetaIds: idsCarpetasDesdeDetail(detail),
      });
      obraMetaDirtyRef.current = false;
      setObraMetaDirty(false);
      await persistAllPagesToServer(detail);
      await loadDetail(selectedId);
      await loadList();
      await loadCarpetas();
      toaster.create({
        title: 'Obra guardada',
        description: detail.paginas?.length
          ? 'También se ha guardado el etiquetado de todas las páginas.'
          : undefined,
        type: 'success',
        duration: detail.paginas?.length ? 6000 : 4000,
        closable: true,
      });
    } catch (e) {
      toaster.create({ title: 'Error al guardar', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const pasarObraAListo = async () => {
    if (!selectedId || !detail) return;
    if (detail.estado === 'LISTO') return;
    const paginas = detail.paginas || [];
    if (paginas.length === 0) {
      toaster.create({
        title: 'Añade páginas primero',
        description: 'Añade al menos una página con «Añadir páginas» antes de marcar la obra como lista.',
        type: 'warning',
      });
      return;
    }
    const sinEtiquetar = paginas.filter((p) => !(Array.isArray(p.instrumentosVoces) && p.instrumentosVoces.length)).length;
    if (sinEtiquetar > 0) {
      onDialogListoOpen();
      return;
    }
    await confirmarPasarAListo();
  };

  const confirmarPasarAListo = async () => {
    onDialogListoClose();
    try {
      setSaving(true);
      const metaPatch =
        obraMetaDirtyRef.current && detail
          ? {
              titulo: detail.titulo,
              autor: detail.autor,
              notasDirector: detail.notasDirector,
              generoId: detail.generoId || null,
              carpetaIds: idsCarpetasDesdeDetail(detail),
            }
          : {};
      const res = await bandasAPI.updateObraPartitura(selectedId, { estado: 'LISTO', ...metaPatch });
      obraMetaDirtyRef.current = false;
      setObraMetaDirty(false);
      setDetail(res.data);
      await loadList();
      toaster.create({
        title: 'Obra en listo',
        description: 'Ya puede mostrarse en el repertorio compartido si tienes el enlace activo.',
        type: 'success',
      });
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo actualizar el estado',
        description: Array.isArray(msg) ? msg[0] : msg || e.message,
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const pasarObraABorrador = async () => {
    onDialogBorradorClose();
    try {
      setSaving(true);
      const metaPatch =
        obraMetaDirtyRef.current && detail
          ? {
              titulo: detail.titulo,
              autor: detail.autor,
              notasDirector: detail.notasDirector,
              generoId: detail.generoId || null,
              carpetaIds: idsCarpetasDesdeDetail(detail),
            }
          : {};
      const res = await bandasAPI.updateObraPartitura(selectedId, { estado: 'BORRADOR', ...metaPatch });
      obraMetaDirtyRef.current = false;
      setObraMetaDirty(false);
      setDetail(res.data);
      await loadList();
      toaster.create({
        title: 'Obra en borrador',
        description: 'La obra ya no aparece en el repertorio compartido hasta que la vuelvas a pasar a listo.',
        type: 'info',
      });
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo actualizar el estado',
        description: Array.isArray(msg) ? msg[0] : msg || e.message,
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCrearInstrumentoPersonalizado = async () => {
    const n = nuevoInstrumentoNombre.trim();
    if (!n) {
      toaster.create({ title: 'Escribe el nombre del instrumento', type: 'warning' });
      return;
    }
    try {
      setInstPersMutating(true);
      const createRes = await instrumentosAPI.createPersonalizado({
        nombre: n,
        familia: nuevoInstrumentoFamilia,
      });
      const created = createRes?.data;
      setNuevoInstrumentoNombre('');
      toaster.create({ title: 'Instrumento creado', type: 'success' });
      const listRes = await instrumentosAPI.getPersonalizados();
      let list = asInstrumentosPersonalizadosList(listRes.data);
      if (created?.id && !list.some((x) => x.id === created.id)) {
        list = [...list, created].sort((a, b) =>
          (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }),
        );
      }
      setInstrumentosPersonalizados(list);
      const res = await instrumentosAPI.getAll(profile?.tipoGrupo ? { tipoGrupo: profile.tipoGrupo } : {});
      setInstrumentCatalog(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo crear el instrumento',
        description: Array.isArray(msg) ? msg[0] : msg || e.message,
        type: 'error',
      });
    } finally {
      setInstPersMutating(false);
    }
  };

  const handleEliminarInstrumentoPersonalizado = async (id, nombre) => {
    setPendingCatalogDelete({ type: 'instrumento', id, nombre: nombre || '' });
    onDialogConfirmCatalogDeleteOpen();
  };

  const handleCrearCarpeta = async () => {
    const n = nuevaCarpetaNombre.trim();
    if (!n) {
      toaster.create({ title: 'Escribe un nombre para la carpeta', type: 'warning' });
      return false;
    }
    try {
      setCarpetasMutating(true);
      const desc = nuevaCarpetaDescripcion.trim();
      await bandasAPI.createPartituraCarpeta({
        nombre: n,
        ...(desc ? { descripcion: desc } : {}),
      });
      setNuevaCarpetaNombre('');
      setNuevaCarpetaDescripcion('');
      await loadCarpetas();
      toaster.create({ title: 'Carpeta creada', type: 'success' });
      return true;
    } catch (e) {
      toaster.create({
        title: 'No se pudo crear la carpeta',
        description: e.response?.data?.message,
        type: 'error',
      });
      return false;
    } finally {
      setCarpetasMutating(false);
    }
  };

  const handleCrearCarpetaFromModal = async () => {
    const ok = await handleCrearCarpeta();
    if (ok) onModalCrearCarpetaClose();
  };

  const handleRenombrarCarpetaGuardar = async () => {
    if (!selectedCarpetaId) return;
    const n = renombrarCarpetaTexto.trim();
    if (!n) {
      toaster.create({ title: 'Nombre vacío', type: 'warning' });
      return;
    }
    try {
      setCarpetasMutating(true);
      await bandasAPI.updatePartituraCarpeta(selectedCarpetaId, {
        nombre: n,
        descripcion: renombrarCarpetaDescripcion.trim() || null,
      });
      setCarpetaViewMode('ficha');
      await loadCarpetas();
      await loadList();
      if (selectedId) await loadDetail(selectedId);
      toaster.create({ title: 'Carpeta actualizada', type: 'success' });
    } catch (e) {
      toaster.create({ title: 'Error al renombrar', description: e.response?.data?.message, type: 'error' });
    } finally {
      setCarpetasMutating(false);
    }
  };

  const confirmarVaciarCarpeta = async () => {
    if (!selectedCarpetaId) return;
    onDialogVaciarCarpetaClose();
    try {
      setCarpetasMutating(true);
      await bandasAPI.setPartituraCarpetaObras(selectedCarpetaId, { obraIds: [] });
      if (drawerCarpetaObras?.id === selectedCarpetaId) {
        setCarpetaDrawerOrdenIds([]);
      }
      await loadCarpetas();
      await loadList();
      if (selectedId) await loadDetail(selectedId);
      toaster.create({
        title: 'Carpeta vaciada',
        description: 'Se han quitado todas las obras de la carpeta. Las obras siguen en tu biblioteca.',
        type: 'success',
      });
    } catch (e) {
      toaster.create({
        title: 'No se pudo vaciar la carpeta',
        description: e.response?.data?.message,
        type: 'error',
      });
    } finally {
      setCarpetasMutating(false);
    }
  };

  const handleEliminarCarpeta = async (id, nombre) => {
    setPendingCatalogDelete({ type: 'carpeta', id, nombre: nombre || '' });
    onDialogConfirmCatalogDeleteOpen();
  };

  const handleGuardarObrasEnCarpeta = async () => {
    if (!drawerCarpetaObras) return;
    try {
      setCarpetasMutating(true);
      await bandasAPI.setPartituraCarpetaObras(drawerCarpetaObras.id, { obraIds: carpetaDrawerOrdenIds });
      cerrarGestionObrasCarpeta();
      await loadCarpetas();
      await loadList();
      if (selectedId) await loadDetail(selectedId);
      toaster.create({ title: 'Obras de la carpeta guardadas', type: 'success' });
    } catch (e) {
      toaster.create({ title: 'Error al guardar obras en carpeta', type: 'error' });
    } finally {
      setCarpetasMutating(false);
    }
  };

  const handleDescargarPartichellasPdf = async () => {
    if (!partichellasInstrumentSlug) {
      toaster.create({ title: 'Elige instrumento o «Todas las páginas»', type: 'warning' });
      return;
    }
    if (!partichellaObraIdsOrdenados.length) {
      toaster.create({ title: 'No hay obras en el listado filtrado', type: 'warning' });
      return;
    }
    try {
      setPartichellasPdfLoading(true);
      const body =
        partichellasInstrumentSlug === PARTICHELLAS_INSTR_TODAS
          ? { obraIds: partichellaObraIdsOrdenados, todasLasPaginas: true }
          : {
              obraIds: partichellaObraIdsOrdenados,
              instrumentoEtiqueta: partichellasInstrumentSlug,
              ...(partichellasVocesPreview.length > 1 && partichellasVocesSel.length > 0
                ? { vozEtiquetas: partichellasVocesSel }
                : {}),
            };
      const res = await bandasAPI.downloadPartichellasPdf(body);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const todas = partichellasInstrumentSlug === PARTICHELLAS_INSTR_TODAS;
      const instrumentLabel = todas
        ? null
        : (() => {
            const found = instrumentosParaEtiquetado.find(
              (i) => etiquetaSlugDesdeNombre(i.nombre) === partichellasInstrumentSlug,
            );
            return found?.nombre || humanizeSlugLabel(partichellasInstrumentSlug) || partichellasInstrumentSlug;
          })();
      const carpetaNombre = carpetaFiltroId
        ? carpetasCatalog.find((c) => c.id === carpetaFiltroId)?.nombre
        : null;
      a.href = url;
      a.download = buildPartituraDownloadFilename({
        primary: carpetaNombre || 'partichellas',
        instrumentLabel,
        todas,
        ext: 'pdf',
        dateSuffix: new Date().toISOString().slice(0, 10),
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toaster.create({ title: 'PDF generado', type: 'success' });
      setModalPartichellasAbierto(false);
    } catch (e) {
      let msg = e.response?.data?.message;
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          const j = JSON.parse(t);
          msg = j.message || t;
        } catch {
          msg = undefined;
        }
      }
      toaster.create({ title: 'No se pudo generar el PDF', description: msg, type: 'error', duration: 7000, closable: true });
    } finally {
      setPartichellasPdfLoading(false);
    }
  };

  const savePage = async (patch) => {
    if (!selectedId || !selectedPageId) return;
    try {
      setSaving(true);
      await bandasAPI.updateObraPartituraPagina(selectedId, selectedPageId, patch);
      // Aplica el patch solo en la página guardada, sin recargar el servidor,
      // para no perder las etiquetas no guardadas del resto de páginas
      // y sincroniza el snapshot del servidor para esa página (evita "dirty" fantasma).
      try {
        const raw = serverPaginasSnapshot.current;
        if (raw) {
          const snap = JSON.parse(String(raw));
          if (Array.isArray(snap)) {
            for (const row of snap) {
              if (row?.id !== selectedPageId) continue;
              if (Object.prototype.hasOwnProperty.call(patch, 'instrumentosVoces')) row.iv = patch.instrumentosVoces ?? [];
              if (Object.prototype.hasOwnProperty.call(patch, 'notasDirector')) row.nd = patch.notasDirector ?? null;
              if (Object.prototype.hasOwnProperty.call(patch, 'tituloObra')) row.to = patch.tituloObra ?? null;
              if (Object.prototype.hasOwnProperty.call(patch, 'autor')) row.au = patch.autor ?? null;
              if (Object.prototype.hasOwnProperty.call(patch, 'ocrTexto')) row.ocr = patch.ocrTexto ?? null;
              break;
            }
            serverPaginasSnapshot.current = JSON.stringify(snap);
          }
        }
      } catch {
        /* ignore */
      }
      setDetail((prev) => ({
        ...prev,
        paginas: prev.paginas.map((p) =>
          p.id === selectedPageId ? { ...p, ...patch } : p,
        ),
      }));
      toaster.create({ title: 'Página actualizada', type: 'success' });
    } catch (e) {
      toaster.create({ title: 'Error al guardar página', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const rotatePage = async () => {
    if (!selectedId || !selectedPage) return;
    const i = ROTATIONS.indexOf(selectedPage.rotacion || 0);
    const next = ROTATIONS[(i + 1) % ROTATIONS.length];
    // Actualiza la rotación en memoria de inmediato para que el visor responda al instante
    setDetail((prev) => ({
      ...prev,
      paginas: prev.paginas.map((p) =>
        p.id === selectedPage.id ? { ...p, rotacion: next } : p,
      ),
    }));
    // Persiste solo la rotación en el servidor sin recargar (para no perder etiquetas no guardadas)
    try {
      await bandasAPI.updateObraPartituraPagina(selectedId, selectedPage.id, { rotacion: next });
    } catch {
      // Si falla, revierte la rotación en memoria
      setDetail((prev) => ({
        ...prev,
        paginas: prev.paginas.map((p) =>
          p.id === selectedPage.id ? { ...p, rotacion: selectedPage.rotacion || 0 } : p,
        ),
      }));
      toaster.create({ title: 'Error al rotar la página', type: 'error' });
    }
  };

  const rotateAllPages90 = async () => {
    if (!selectedId || !detail?.paginas?.length) return;
    const prevRotaciones = new Map(detail.paginas.map((p) => [p.id, Number(p.rotacion) || 0]));
    const nextById = new Map(
      detail.paginas.map((p) => {
        const current = Number(p.rotacion) || 0;
        const i = ROTATIONS.indexOf(current);
        const next = ROTATIONS[(i + 1) % ROTATIONS.length];
        return [p.id, next];
      }),
    );

    // Actualiza en memoria de inmediato para feedback instantáneo.
    setDetail((prev) => ({
      ...prev,
      paginas: prev.paginas.map((p) => ({ ...p, rotacion: nextById.get(p.id) ?? (Number(p.rotacion) || 0) })),
    }));

    try {
      setBulkActionStatus({ active: true, label: 'Rotando todas las páginas…' });
      setSaving(true);
      await Promise.all(
        detail.paginas.map((p) =>
          bandasAPI.updateObraPartituraPagina(selectedId, p.id, { rotacion: nextById.get(p.id) ?? 0 }),
        ),
      );
      toaster.create({ title: 'Todas las páginas rotadas 90°', type: 'success' });
    } catch {
      // Si falla alguna petición, revierte todas en memoria para mantener consistencia.
      setDetail((prev) => ({
        ...prev,
        paginas: prev.paginas.map((p) => ({ ...p, rotacion: prevRotaciones.get(p.id) ?? 0 })),
      }));
      toaster.create({ title: 'Error al rotar todas las páginas', type: 'error' });
    } finally {
      setBulkActionStatus({ active: false, label: '' });
      setSaving(false);
    }
  };

  const initRangoDesdePagina = (page) => {
    if (!page) return;
    setRangoPatch({
      instrumentosVoces: Array.isArray(page.instrumentosVoces) ? page.instrumentosVoces : [],
      notasDirector: page.notasDirector || '',
    });
    setRangoOrdenesTexto(String(page.orden));
  };

  const aplicarEtiquetasVariosTramos = async () => {
    if (!selectedId || !detail?.paginas) return;
    const parsed = parseTramosOrdenes(rangoOrdenesTexto, detail.paginas);
    if (!parsed.ok) {
      toaster.create({ title: parsed.error, type: 'warning', duration: 6000, closable: true });
      return;
    }

    const ivPatch = Array.isArray(rangoPatch.instrumentosVoces) ? rangoPatch.instrumentosVoces : [];
    const ndPatch = rangoPatch.notasDirector?.trim() || null;

    // Calcula el conjunto de órdenes afectados por todos los tramos
    const ordenesAfectados = new Set();
    for (const { desde, hasta } of parsed.tramos) {
      for (let o = desde; o <= hasta; o++) ordenesAfectados.add(o);
    }

    try {
      setSaving(true);
      for (const { desde, hasta } of parsed.tramos) {
        await bandasAPI.aplicarEtiquetasRangoObraPartitura(selectedId, {
          ordenDesde: desde,
          ordenHasta: hasta,
          instrumentosVoces: ivPatch,
          notasDirector: ndPatch,
        });
      }
      // Aplica los cambios solo en las páginas del rango, sin recargar el servidor
      // (así se conservan las etiquetas no guardadas del resto de páginas)
      setDetail((prev) => ({
        ...prev,
        paginas: prev.paginas.map((p) =>
          ordenesAfectados.has(p.orden)
            ? { ...p, instrumentosVoces: ivPatch, notasDirector: ndPatch }
            : p,
        ),
      }));
      toaster.create({ title: 'Etiquetas aplicadas al rango', type: 'success' });
    } catch (e) {
      toaster.create({ title: 'Error al aplicar rango', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const deleteObra = async (id) => {
    setObraIdPendienteEliminar(id);
    onDialogEliminarObraOpen();
  };

  const confirmarEliminarObra = async () => {
    if (!obraIdPendienteEliminar) return;
    try {
      setDeletingObra(true);
      await bandasAPI.deleteObraPartitura(obraIdPendienteEliminar);
      if (selectedId === obraIdPendienteEliminar) {
        ejecutarSalidaObra({ type: 'list' });
      }
      await loadList();
      await loadCarpetas();
      toaster.create({ title: 'Obra eliminada', type: 'success' });
    } catch {
      toaster.create({ title: 'Error al eliminar', type: 'error' });
    } finally {
      setDeletingObra(false);
      setObraIdPendienteEliminar(null);
      onDialogEliminarObraClose();
    }
  };

  const deletePage = async (paginaId) => {
    if (!selectedId) return;
    try {
      await bandasAPI.deleteObraPartituraPagina(selectedId, paginaId);
      setSelectedPageId(null);
      await loadDetail(selectedId);
      toaster.create({ title: 'Página eliminada', type: 'success' });
    } catch {
      toaster.create({ title: 'Error', type: 'error' });
    }
  };

  const cargandoObraSeleccionada = Boolean(selectedId && !detail);

  const isTitularBanda = useMemo(
    () =>
      Boolean(
        user?.userType === 'BANDA' &&
          (!profile?.userId || String(user?.id) === String(profile.userId)),
      ),
    [user?.userType, user?.id, profile?.userId],
  );

  /** Catálogo general + personalizados, sin repetir el mismo nombre (el catálogo gana). */
  const instrumentosParaEtiquetado = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const i of instrumentCatalog || []) {
      const key = etiquetaSlugDesdeNombre(i.nombre);
      if (!key || seen.has(key)) continue;
      // Guión es voz, no instrumento (legado filtrado).
      if (key === 'guion') continue;
      seen.add(key);
      out.push({ ...i, esPersonalizado: false });
    }
    for (const i of instrumentosPersonalizados || []) {
      const key = etiquetaSlugDesdeNombre(i.nombre);
      if (!key || seen.has(key)) continue;
      if (key === 'guion') continue;
      seen.add(key);
      out.push({ ...i, esPersonalizado: true });
    }
    return out;
  }, [instrumentCatalog, instrumentosPersonalizados]);

  const copyRepertorioShareUrl = async () => {
    if (!repertorioShare.shareUrl) return;
    try {
      await navigator.clipboard.writeText(repertorioShare.shareUrl);
      toaster.create({ title: 'Enlace copiado', type: 'success' });
    } catch {
      toaster.create({ title: 'No se pudo copiar al portapapeles', type: 'warning' });
    }
  };

  const shareRepertorioPorWhatsApp = () => {
    const url = (repertorioShare.shareUrl || '').trim();
    if (!url) return;
    const nombreBanda = (profile?.nombre || '').trim() || 'tu banda';
    const text = `Accede al repertorio de *${nombreBanda}* con la clave que te haya facilitado el administrador.\n\n${url}`;
    const encoded = encodeURIComponent(text);
    // `whatsapp://` abre la app de escritorio o móvil ya instalada (sin pasar por el login de WhatsApp Web).
    const deepLink = `whatsapp://send?text=${encoded}`;
    const a = document.createElement('a');
    a.href = deepLink;
    a.setAttribute('rel', 'noopener noreferrer');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const descargarQrRepertorio = async (sharePassword) => {
    const url = (repertorioShare.shareUrl || '').trim();
    if (!url) return;
    const nombreBanda = (profile?.nombre || '').trim() || 'tu banda';
    setGenerandoQrRepertorio(true);
    try {
      const blob = await generateRepertorioShareQrPng({
        url,
        bandName: nombreBanda,
        sharePassword: sharePassword?.trim() || undefined,
      });
      const safeName = nombreBanda
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .toLowerCase() || 'banda';
      const suffix = sharePassword?.trim() ? '-con-clave' : '';
      downloadBlob(blob, `repertorio-qr-${safeName}${suffix}.png`);
      toaster.create({ title: 'QR descargado', type: 'success' });
    } catch (e) {
      toaster.create({
        title: 'No se pudo generar el QR',
        description: e?.message || 'Inténtalo de nuevo',
        type: 'error',
      });
    } finally {
      setGenerandoQrRepertorio(false);
    }
  };

  const abrirDialogoGenerarQr = () => {
    if (!repertorioShare.shareUrl?.trim()) return;
    onDialogQrIncluirPwdOpen();
  };

  const onQrIncluirPwdNo = () => {
    onDialogQrIncluirPwdClose();
    void descargarQrRepertorio();
  };

  const onQrIncluirPwdSi = () => {
    onDialogQrIncluirPwdClose();
    if (!repertorioShare.canRevealSharePassword) {
      toaster.create({
        title: 'No se puede incluir la contraseña',
        description:
          'Esta clave del enlace se guardó antes de poder mostrarla. Cambia la contraseña del enlace con «Cambiar contraseña» y vuelve a intentarlo, o genera el QR sin contraseña.',
        type: 'warning',
        duration: 8000,
        closable: true,
      });
      return;
    }
    setQrPwdAccount('');
    onModalQrPwdOpen();
  };

  const closeQrPwdModal = () => {
    setQrPwdAccount('');
    onModalQrPwdClose();
  };

  const openYoutubeObraModal = () => {
    setYoutubeUrlDraft(detail?.youtubeUrl || '');
    onModalYoutubeObraOpen();
  };

  const closeYoutubeObraModal = () => {
    setYoutubeUrlDraft('');
    onModalYoutubeObraClose();
  };

  const guardarYoutubeObra = async () => {
    if (!selectedId) return;
    const trimmed = youtubeUrlDraft.trim();
    const normalized = trimmed ? normalizeYouTubeWatchUrl(trimmed) : null;
    if (trimmed && !normalized) {
      toaster.create({
        title: 'URL de YouTube no válida',
        description: 'Pega un enlace de youtube.com o youtu.be',
        type: 'warning',
      });
      return;
    }
    try {
      setYoutubeSaving(true);
      await bandasAPI.updateObraPartitura(selectedId, { youtubeUrl: normalized });
      setDetail((prev) => (prev ? { ...prev, youtubeUrl: normalized } : prev));
      await loadList();
      closeYoutubeObraModal();
      toaster.create({
        title: normalized ? 'Vídeo de YouTube guardado' : 'Vídeo eliminado',
        type: 'success',
      });
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo guardar el vídeo',
        description: Array.isArray(msg) ? msg[0] : msg || e?.message,
        type: 'error',
      });
    } finally {
      setYoutubeSaving(false);
    }
  };

  const quitarYoutubeObra = async () => {
    if (!selectedId || !detail?.youtubeUrl) return;
    try {
      setYoutubeSaving(true);
      await bandasAPI.updateObraPartitura(selectedId, { youtubeUrl: null });
      setDetail((prev) => (prev ? { ...prev, youtubeUrl: null } : prev));
      await loadList();
      toaster.create({ title: 'Vídeo eliminado', type: 'success' });
    } catch {
      toaster.create({ title: 'No se pudo quitar el vídeo', type: 'error' });
    } finally {
      setYoutubeSaving(false);
    }
  };

  const submitQrRevealAndGenerate = async () => {
    if (!qrPwdAccount) {
      toaster.create({ title: 'Indica tu contraseña de cuenta', type: 'warning' });
      return;
    }
    try {
      setQrPwdLoading(true);
      const res = await bandasAPI.revealRepertorioSharePassword({ accountPassword: qrPwdAccount });
      const pwd = res.data?.sharePassword || '';
      if (!pwd) {
        toaster.create({ title: 'No se obtuvo la contraseña del enlace', type: 'error' });
        return;
      }
      closeQrPwdModal();
      await descargarQrRepertorio(pwd);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message;
      const text = Array.isArray(msg) ? msg[0] : msg;
      toaster.create({
        title: status === 401 ? 'Contraseña de cuenta incorrecta' : 'No se pudo generar el QR',
        description: text || 'Inténtalo de nuevo.',
        type: 'error',
      });
    } finally {
      setQrPwdLoading(false);
    }
  };

  const bumpMusicoUltimoCompartido = (musicoId, iso) => {
    if (!iso) return;
    setMusicoEnlaces((prev) => ({
      ...prev,
      musicos: prev.musicos.map((m) => (m.id === musicoId ? { ...m, repertorioUltimoCompartidoAt: iso } : m)),
    }));
  };

  const formatUltimoCompartido = (iso) => {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
    } catch {
      return '—';
    }
  };

  const copyEnlaceIndividualMusico = async (m) => {
    try {
      await navigator.clipboard.writeText(m.shareUrl);
      const reg = await bandasAPI.registrarRepertorioShareMusicoCompartido(m.id);
      bumpMusicoUltimoCompartido(m.id, reg.data?.repertorioUltimoCompartidoAt);
      toaster.create({ title: 'Enlace copiado', type: 'success' });
    } catch {
      toaster.create({ title: 'No se pudo copiar el enlace', type: 'warning' });
    }
  };

  const shareWhatsappIndividualMusico = (m) => {
    const nombreBanda = (profile?.nombre || '').trim() || 'tu banda';
    const text = `Accede al repertorio de *${nombreBanda}* con la clave que te haya facilitado el administrador.\n\n${m.shareUrl}`;
    const encoded = encodeURIComponent(text);
    const digits = whatsappE164DigitsFromTelefono(m.telefono);
    const href = digits ? `https://wa.me/${digits}?text=${encoded}` : `whatsapp://send?text=${encoded}`;
    if (digits) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      const a = document.createElement('a');
      a.href = href;
      a.setAttribute('rel', 'noopener noreferrer');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    bandasAPI
      .registrarRepertorioShareMusicoCompartido(m.id)
      .then((reg) => bumpMusicoUltimoCompartido(m.id, reg.data?.repertorioUltimoCompartidoAt))
      .catch(() => {});
  };

  const closeVerPwdModal = () => {
    setVerPwdAccount('');
    setVerPwdRevealed('');
    onModalVerPwdClose();
  };

  const openVerPwdModal = () => {
    setVerPwdAccount('');
    setVerPwdRevealed('');
    onModalVerPwdOpen();
  };

  const submitRevealRepertorioSharePassword = async () => {
    if (!verPwdAccount) {
      toaster.create({ title: 'Indica tu contraseña de cuenta', type: 'warning' });
      return;
    }
    try {
      setVerPwdLoading(true);
      const res = await bandasAPI.revealRepertorioSharePassword({ accountPassword: verPwdAccount });
      const pwd = res.data?.sharePassword || '';
      setVerPwdRevealed(pwd);
      setVerPwdAccount('');
      if (pwd) {
        toaster.create({ title: 'Contraseña del enlace mostrada abajo', type: 'success' });
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message;
      const text = Array.isArray(msg) ? msg[0] : msg;
      toaster.create({
        title: status === 401 ? 'Contraseña de cuenta incorrecta' : 'No se pudo mostrar la contraseña',
        description: text || 'Inténtalo de nuevo.',
        type: 'error',
      });
    } finally {
      setVerPwdLoading(false);
    }
  };

  const copyRevealedSharePassword = async () => {
    if (!verPwdRevealed) return;
    try {
      await navigator.clipboard.writeText(verPwdRevealed);
      toaster.create({ title: 'Contraseña copiada', type: 'success' });
    } catch {
      toaster.create({ title: 'No se pudo copiar al portapapeles', type: 'warning' });
    }
  };

  const openCompartirRepertorioModal = () => {
    setSharePwd('');
    setSharePwd2('');
    onModalShareOpen();
  };

  const submitRepertorioSharePassword = async () => {
    if (sharePwd.length < 8) {
      toaster.create({ title: 'La contraseña debe tener al menos 8 caracteres', type: 'warning' });
      return;
    }
    if (sharePwd !== sharePwd2) {
      toaster.create({ title: 'Las contraseñas no coinciden', type: 'warning' });
      return;
    }
    try {
      setSharePwdSaving(true);
      await bandasAPI.setRepertorioSharePassword({ password: sharePwd, passwordConfirm: sharePwd2 });
      toaster.create({
        title: 'Contraseña guardada',
        description:
          'El enlace ya está protegido. Apunta la contraseña en un lugar seguro: por seguridad no la guardamos en texto plano y no podremos mostrártela después.',
        type: 'success',
        duration: 9000,
      });
      await loadRepertorioShare();
      void loadMusicoEnlaces();
      onModalShareClose();
      setSharePwd('');
      setSharePwd2('');
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo guardar',
        description: Array.isArray(msg) ? msg[0] : msg || 'Inténtalo de nuevo.',
        type: 'error',
      });
    } finally {
      setSharePwdSaving(false);
    }
  };

  const openCambiarPwdModal = () => {
    setCambiarActual('');
    setCambiarNueva('');
    setCambiarNueva2('');
    onModalCambiarPwdOpen();
  };

  const submitCambiarRepertorioSharePassword = async () => {
    if (cambiarNueva.length < 8) {
      toaster.create({ title: 'La nueva contraseña debe tener al menos 8 caracteres', type: 'warning' });
      return;
    }
    if (cambiarNueva !== cambiarNueva2) {
      toaster.create({ title: 'Las nuevas contraseñas no coinciden', type: 'warning' });
      return;
    }
    try {
      setCambiarSaving(true);
      await bandasAPI.updateRepertorioSharePassword({
        currentPassword: cambiarActual,
        newPassword: cambiarNueva,
        newPasswordConfirm: cambiarNueva2,
      });
      toaster.create({
        title: 'Contraseña actualizada',
        description: 'Vuelve a guardar la nueva contraseña en tu gestor seguro.',
        type: 'success',
        duration: 6000,
      });
      await loadRepertorioShare();
      void loadMusicoEnlaces();
      onModalCambiarPwdClose();
      setCambiarActual('');
      setCambiarNueva('');
      setCambiarNueva2('');
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo actualizar',
        description: Array.isArray(msg) ? msg[0] : msg || 'Comprueba la contraseña actual.',
        type: 'error',
      });
    } finally {
      setCambiarSaving(false);
    }
  };

  return (
    <AppLayout userType="banda" userName={profile?.nombre || 'Mi Banda'}>
      <Container maxW="7xl" py={8}>
        <VStack align="stretch" gap={8}>
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <Heading size="lg">
              <FaMusic style={{ display: 'inline', marginRight: 8 }} />
              Archivo musical (partituras)
            </Heading>
          </HStack>

          <Tabs.Root
            value={tabsControlledValue(archivoMusicaTabIndex)}
            onValueChange={tabsOnValueChange(setArchivoMusicaTabIndex)}
            colorPalette="purple"
            variant='enclosed'
            w="full"
          >
            <Tabs.List flexWrap="wrap" data-tour="repertorio-tabs">
              <Tab value="0" fontWeight="600">Biblioteca</Tab>
              <Tab value="1" fontWeight="600">Carpetas</Tab>
              <Tab value="2" fontWeight="600">Voces e instrumentos</Tab>
              <Tab value="3" fontWeight="600">Compartir repertorio</Tab>
              <Tab value="4" fontWeight="600">Estadísticas</Tab>
            </Tabs.List>
            <TabPanels>
              <TabPanel value="0" px={0} pt={4}>
            <Card.Root bg={cardBg} shadow="md" rounded="2xl" w="full" minW={0}>
              <Card.Header>
                {cargandoObraSeleccionada ? (
                  <HStack gap={3}>
                    <Spinner size="sm" />
                    <Heading size="md">Cargando obra…</Heading>
                  </HStack>
                ) : detail ? (
                  <HStack gap={3} align="flex-start" flexWrap="wrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={
                        obraViewMode === 'edit'
                          ? () => intentarSalirObraBorrador({ type: 'ficha' })
                          : goToBrowseList
                      }
                      flexShrink={0}><FaArrowLeft />{obraViewMode === 'edit' ? 'Volver a la ficha' : 'Volver al listado'}</Button>
                    <Box flex={1} minW={0}>
                      {obraViewMode === 'edit' ? (
                        <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" letterSpacing="wide">
                          Editar obra
                        </Text>
                      ) : null}
                      <Heading size="md" lineClamp={2}>
                        {detail.titulo}
                      </Heading>
                    </Box>
                    <Badge
                      fontSize="0.7rem"
                      alignSelf="center"
                      colorPalette={detail.estado === 'LISTO' ? 'green' : 'gray'}
                    >
                      {detail.estado === 'LISTO' ? 'Listo' : 'Borrador'}
                    </Badge>
                    {obraViewMode === 'ficha' ? (
                      <Button
                        size="sm"
                        colorPalette="purple"
                        variant="outline"
                        onClick={() => setObraViewMode('edit')}
                        flexShrink={0}><FaEdit />Editar
                                              </Button>
                    ) : detail.estado !== 'LISTO' ? (
                      <Button
                        size="sm"
                        colorPalette="green"
                        onClick={pasarObraAListo}
                        loading={saving}
                        flexShrink={0}><FaCheckCircle />Pasar a listo
                                              </Button>
                    ) : (
                      <Button
                        size="sm"
                        colorPalette="gray"
                        variant="outline"
                        onClick={onDialogBorradorOpen}
                        loading={saving}
                        flexShrink={0}
                      >
                        Volver a borrador
                      </Button>
                    )}
                  </HStack>
                ) : (
                  <VStack align="stretch" gap={1}>
                    <Heading size="md">Obras en repertorio</Heading>
                    <Text fontSize="sm" color="gray.600">
                      Busca por título, autor o notas; filtra por género y estado; elige lista o mosaico. Pulsa una obra
                      para ver su ficha o activa «Páginas sueltas» para explorar cada página del archivo.
                    </Text>
                  </VStack>
                )}
              </Card.Header>
              <Card.Body pt={0}>
                {cargandoObraSeleccionada ? (
                  <Flex justify="center" align="center" minH="200px">
                    <Spinner size="lg" color="purple.500" />
                  </Flex>
                ) : !detail ? (
                  <VStack align="stretch" gap={5}>
                    <VStack align="stretch" gap={2}>
                      <HStack gap={2} flexWrap="wrap">
                        <Button
                          colorPalette="purple"
                          size="sm"
                          onClick={() => setAltaObraWizardOpen(true)}
                          flexShrink={0}
                          data-tour="repertorio-add"
                          data-testid="repertorio-add-obras"><FaPlus />Añadir obras
                                                  </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          colorPalette="purple"
                          onClick={onObrasExportOpen}
                          flexShrink={0}
                          disabled={loading || obras.length === 0}><FaDownload />Descargar listado
                                                  </Button>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        Sube PDFs o imágenes de varias obras a la vez. La IA detectará las obras, extraerá título, autor
                        y etiquetas de cada página automáticamente. Las obras se crearán en borrador para que las revises.
                      </Text>
                    </VStack>
                    <Box borderWidth="1px" rounded="xl" p={4} bg={filterPanelBg} borderColor="gray.200" data-tour="repertorio-filtros">
                      <Flex justify="space-between" align="center" flexWrap="wrap" gap={3} mb={3}>
                        <Heading size="sm">Filtros</Heading>
                        <HStack gap={3}>
                          <Text
                            fontSize="sm"
                            color={bibliotecaModo === 'obras' ? 'purple.600' : 'gray.500'}
                            fontWeight={bibliotecaModo === 'obras' ? '600' : 'normal'}
                          >
                            Obras completas
                          </Text>
                          <Switch
                            colorPalette="purple"
                            checked={bibliotecaModo === 'paginas'}
                            onChange={(e) => {
                              const paginas = e.target.checked;
                              setBibliotecaModo(paginas ? 'paginas' : 'obras');
                              if (!paginas) setPaginaInstrumentoFilter('');
                            }}
                          />
                          <Text
                            fontSize="sm"
                            color={bibliotecaModo === 'paginas' ? 'purple.600' : 'gray.500'}
                            fontWeight={bibliotecaModo === 'paginas' ? '600' : 'normal'}
                          >
                            Páginas sueltas
                          </Text>
                        </HStack>
                      </Flex>
                      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr auto' }} gap={4} alignItems="flex-end">
                        <Field.Root>
                          <Field.Label fontSize="xs" mb={1}>
                            Buscar
                          </Field.Label>
                          <Input
                            size="sm"
                            placeholder="Título, autor, notas del director…"
                            value={obraSearch}
                            onChange={(e) => setObraSearch(e.target.value)}
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label fontSize="xs" mb={1}>
                            Género
                          </Field.Label>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={obraGeneroFilterId}
                              onChange={(e) => setObraGeneroFilterId(e.target.value)}>
                              <option value="">Todos los géneros</option>
                              {generosCatalog.map((g) => (
                                <option key={g.id} value={g.id}>
                                  {g.nombre}
                                </option>
                              ))}
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Field.Root>
                        <Field.Root>
                          <Field.Label fontSize="xs" mb={1}>
                            Estado
                          </Field.Label>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={obraEstadoFilter}
                              onChange={(e) => setObraEstadoFilter(e.target.value)}>
                              <option value="">Todos</option>
                              <option value="BORRADOR">Borrador</option>
                              <option value="PROCESANDO">Procesando</option>
                              <option value="LISTO">Listo</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Field.Root>
                        <Field.Root w={{ base: 'full', md: 'auto' }}>
                          <Field.Label fontSize="xs" mb={1}>
                            Vista
                          </Field.Label>
                          <ButtonGroup size="sm" attached variant="outline">
                            <IconButton
                              aria-label="Vista en listado"
                              onClick={() => setObraVista('list')}
                              variant={obraVista === 'list' ? 'solid' : 'outline'}
                              colorPalette="purple"><FaList /></IconButton>
                            <IconButton
                              aria-label="Vista en mosaico"
                              onClick={() => setObraVista('grid')}
                              variant={obraVista === 'grid' ? 'solid' : 'outline'}
                              colorPalette="purple"><FaTh /></IconButton>
                          </ButtonGroup>
                        </Field.Root>
                      </Grid>
                      <HStack flexWrap="wrap" gap={3} mt={3} align="flex-end">
                        <Field.Root maxW={{ base: 'full', md: '280px' }} flex={1} minW={0}>
                          <Field.Label fontSize="xs" mb={1}>
                            Carpeta
                          </Field.Label>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={carpetaFiltroId}
                              onChange={(e) => setCarpetaFiltroId(e.target.value)}>
                              <option value="">Todas las carpetas</option>
                              {carpetasCatalog.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.nombre}
                                </option>
                              ))}
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Field.Root>
                        {bibliotecaModo === 'paginas' ? (
                          <Field.Root maxW={{ base: 'full', md: '280px' }} flex={1} minW={0}>
                            <Field.Label fontSize="xs" mb={1}>
                              Instrumento
                            </Field.Label>
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                size="sm"
                                value={paginaInstrumentoFilter}
                                onChange={(e) => setPaginaInstrumentoFilter(e.target.value)}>
                                <option value="">Todos los instrumentos</option>
                                {instrumentosParaEtiquetado.map((i) => {
                                  const slug = etiquetaSlugDesdeNombre(i.nombre);
                                  return (
                                    <option key={i.esPersonalizado ? `p-${i.id}` : i.id} value={slug}>
                                      {i.nombre}
                                      {i.esPersonalizado ? ' (personalizado)' : ''}
                                    </option>
                                  );
                                })}
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </Field.Root>
                        ) : null}
                        <Button
                          size="sm"
                          colorPalette="purple"
                          variant="solid"
                          disabled={loading || filteredObras.length === 0}
                          onClick={() => {
                            setModalPartichellasAbierto(true);
                            setPartichellasInstrumentSlug('');
                            setPartichellasVocesPreview([]);
                            setPartichellasVocesSel([]);
                          }}><FaDownload />Descargar partichellas
                                                  </Button>
                      </HStack>
                    </Box>

                    <Box data-tour="repertorio-list">
                    {loading || (bibliotecaModo === 'paginas' && paginasBibliotecaLoading) ? (
                      <Flex justify="center" py={12}>
                        <Spinner size="lg" color="purple.500" />
                      </Flex>
                    ) : bibliotecaModo === 'paginas' ? (
                      filteredPaginas.length === 0 ? (
                        <Text color="gray.500" fontSize="sm">
                          {paginasBiblioteca.length === 0
                            ? 'Aún no hay páginas en el archivo. Sube obras con partituras para verlas aquí.'
                            : 'Ninguna página coincide con los filtros. Prueba otro instrumento o amplía la búsqueda.'}
                        </Text>
                      ) : obraVista === 'list' ? (
                        <VStack align="stretch" gap={2}>
                          <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                            <Text fontSize="xs" color="gray.500">
                              {bibliotecaPagination.total} página{bibliotecaPagination.total === 1 ? '' : 's'}
                              {bibliotecaPagination.total > 0
                                ? ` · Mostrando ${bibliotecaPagination.start}-${bibliotecaPagination.end}`
                                : ''}
                            </Text>
                            <HStack gap={2} flexWrap="wrap">
                              <HStack gap={1}>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() => setObraPage((p) => Math.max(1, p - 1))}
                                  disabled={bibliotecaPagination.page <= 1}
                                >
                                  Anterior
                                </Button>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() =>
                                    setObraPage((p) => Math.min(bibliotecaPagination.totalPages, p + 1))
                                  }
                                  disabled={bibliotecaPagination.page >= bibliotecaPagination.totalPages}
                                >
                                  Siguiente
                                </Button>
                              </HStack>
                              <Text fontSize="xs" color="gray.500">
                                Página {bibliotecaPagination.page} / {bibliotecaPagination.totalPages}
                              </Text>
                              <NativeSelect.Root>
                                <NativeSelect.Field
                                  size="xs"
                                  value={obraPageSize}
                                  onChange={(e) => setObraPageSize(Number(e.target.value) || 25)}
                                  w="120px">
                                  <option value={10}>10 / pág.</option>
                                  <option value={25}>25 / pág.</option>
                                  <option value={50}>50 / pág.</option>
                                  <option value={100}>100 / pág.</option>
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            </HStack>
                          </Flex>
                          {pagedPaginas.map((p) => (
                            <Flex
                              key={p.id}
                              p={3}
                              rounded="lg"
                              borderWidth="1px"
                              align="center"
                              justify="space-between"
                              gap={3}
                              cursor="pointer"
                              borderColor="gray.200"
                              _hover={{ borderColor: 'purple.300', bg: 'purple.50' }}
                              transition="colors 0.15s ease"
                              onClick={() => abrirPaginaBiblioteca(p)}
                            >
                              <HStack align="flex-start" gap={3} minW={0} flex={1}>
                                <Box
                                  w="52px"
                                  h="68px"
                                  rounded="md"
                                  overflow="hidden"
                                  bg="gray.100"
                                  flexShrink={0}
                                  borderWidth="1px"
                                  borderColor="gray.200"
                                >
                                  <ChakraImage
                                    src={pageImageSrc(p)}
                                    alt=""
                                    w="full"
                                    h="full"
                                    objectFit="cover"
                                    transform={`rotate(${p.rotacion || 0}deg)`}
                                    transformOrigin="center"
                                  />
                                </Box>
                                <Box minW={0} flex={1}>
                                  <Text fontWeight="600" fontSize="sm" lineClamp={1}>
                                    {p.obraTitulo}
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    Página {p.orden}
                                    {p.obraAutor ? ` · ${p.obraAutor}` : ''}
                                  </Text>
                                  <HStack gap={1} mt={1} flexWrap="wrap">
                                    {paginaInstrumentoLabels(p).map((label) => (
                                      <Badge key={label} fontSize="0.65rem" variant="outline" colorPalette="purple">
                                        {label}
                                      </Badge>
                                    ))}
                                    {!paginaInstrumentoLabels(p).length ? (
                                      <Text fontSize="xs" color="gray.400">
                                        Sin etiquetar
                                      </Text>
                                    ) : null}
                                  </HStack>
                                </Box>
                              </HStack>
                              <HStack gap={2} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  colorPalette="purple"
                                  onClick={() => openObraFicha(p.obraId)}><FaEye />Ver obra
                                                                  </Button>
                              </HStack>
                            </Flex>
                          ))}
                        </VStack>
                      ) : (
                        <VStack align="stretch" gap={2}>
                          <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                            <Text fontSize="xs" color="gray.500">
                              {bibliotecaPagination.total} página{bibliotecaPagination.total === 1 ? '' : 's'}
                              {bibliotecaPagination.total > 0
                                ? ` · Mostrando ${bibliotecaPagination.start}-${bibliotecaPagination.end}`
                                : ''}
                            </Text>
                            <HStack gap={2} flexWrap="wrap">
                              <HStack gap={1}>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() => setObraPage((p) => Math.max(1, p - 1))}
                                  disabled={bibliotecaPagination.page <= 1}
                                >
                                  Anterior
                                </Button>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() =>
                                    setObraPage((p) => Math.min(bibliotecaPagination.totalPages, p + 1))
                                  }
                                  disabled={bibliotecaPagination.page >= bibliotecaPagination.totalPages}
                                >
                                  Siguiente
                                </Button>
                              </HStack>
                              <Text fontSize="xs" color="gray.500">
                                Página {bibliotecaPagination.page} / {bibliotecaPagination.totalPages}
                              </Text>
                              <NativeSelect.Root>
                                <NativeSelect.Field
                                  size="xs"
                                  value={obraPageSize}
                                  onChange={(e) => setObraPageSize(Number(e.target.value) || 25)}
                                  w="120px">
                                  <option value={10}>10 / pág.</option>
                                  <option value={25}>25 / pág.</option>
                                  <option value={50}>50 / pág.</option>
                                  <option value={100}>100 / pág.</option>
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            </HStack>
                          </Flex>
                          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4 }} gap={4}>
                            {pagedPaginas.map((p) => (
                              <Card.Root
                                key={p.id}
                                variant="outline"
                                borderColor="gray.200"
                                _hover={{ borderColor: 'purple.400', shadow: 'md' }}
                                transition="all 0.15s ease"
                                overflow="hidden"
                              >
                                <AspectRatio ratio={3 / 4}>
                                  <Box
                                    bg="gray.100"
                                    position="relative"
                                    cursor="pointer"
                                    onClick={() => abrirPaginaBiblioteca(p)}
                                  >
                                    <ChakraImage
                                      src={pageImageSrc(p)}
                                      alt={`${p.obraTitulo} pág. ${p.orden}`}
                                      w="full"
                                      h="full"
                                      objectFit="cover"
                                      transform={`rotate(${p.rotacion || 0}deg)`}
                                      transformOrigin="center"
                                    />
                                  </Box>
                                </AspectRatio>
                                <Card.Body py={3}>
                                  <Text fontWeight="600" fontSize="sm" lineClamp={2}>
                                    {p.obraTitulo}
                                  </Text>
                                  <Text fontSize="xs" color="gray.600" mt={1}>
                                    Página {p.orden}
                                  </Text>
                                  <HStack gap={1} mt={2} flexWrap="wrap">
                                    {paginaInstrumentoLabels(p).slice(0, 3).map((label) => (
                                      <Badge key={label} fontSize="0.65rem" variant="outline" colorPalette="purple">
                                        {label}
                                      </Badge>
                                    ))}
                                  </HStack>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    colorPalette="purple"
                                    mt={3}
                                    w="full"
                                    onClick={() => openObraFicha(p.obraId)}><FaEye />Ver obra
                                                                      </Button>
                                </Card.Body>
                              </Card.Root>
                            ))}
                          </SimpleGrid>
                        </VStack>
                      )
                    ) : filteredObras.length === 0 ? (
                      <Text color="gray.500" fontSize="sm">
                        {obras.length === 0
                          ? 'Aún no hay obras. Pulsa «Añadir obra» y sube un PDF o imágenes para crear la primera.'
                          : 'Ninguna obra coincide con los filtros. Prueba a ampliar la búsqueda, el género o el estado.'}
                      </Text>
                    ) : obraVista === 'list' ? (
                      <VStack align="stretch" gap={2}>
                        <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                          <Text fontSize="xs" color="gray.500">
                            {obraPagination.total} obra{obraPagination.total === 1 ? '' : 's'}
                            {obraPagination.total > 0 ? ` · Mostrando ${obraPagination.start}-${obraPagination.end}` : ''}
                          </Text>
                          <HStack gap={2} flexWrap="wrap">
                            <HStack gap={1}>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={() => setObraPage((p) => Math.max(1, p - 1))}
                                disabled={obraPagination.page <= 1}
                              >
                                Anterior
                              </Button>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={() => setObraPage((p) => Math.min(obraPagination.totalPages, p + 1))}
                                disabled={obraPagination.page >= obraPagination.totalPages}
                              >
                                Siguiente
                              </Button>
                            </HStack>
                            <Text fontSize="xs" color="gray.500">
                              Página {obraPagination.page} / {obraPagination.totalPages}
                            </Text>
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                size="xs"
                                value={obraPageSize}
                                onChange={(e) => setObraPageSize(Number(e.target.value) || 25)}
                                w="120px">
                                <option value={10}>10 / pág.</option>
                                <option value={25}>25 / pág.</option>
                                <option value={50}>50 / pág.</option>
                                <option value={100}>100 / pág.</option>
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </HStack>
                        </Flex>
                        {pagedObras.map((o) => (
                          <Flex
                            key={o.id}
                            p={3}
                            rounded="lg"
                            borderWidth="1px"
                            align="center"
                            justify="space-between"
                            gap={3}
                            cursor="pointer"
                            borderColor="gray.200"
                            _hover={{ borderColor: 'purple.300', bg: 'purple.50' }}
                            transition="colors 0.15s ease"
                            onClick={() => openObraFicha(o.id)}
                          >
                            <HStack align="flex-start" gap={3} minW={0} flex={1}>
                              <Box
                                w="52px"
                                h="68px"
                                rounded="md"
                                overflow="hidden"
                                bg="gray.100"
                                flexShrink={0}
                                borderWidth="1px"
                                borderColor="gray.200"
                              >
                                {obraPortada(o) ? (
                                  <ChakraImage
                                    src={pageImageSrc(obraPortada(o))}
                                    alt=""
                                    w="full"
                                    h="full"
                                    objectFit="cover"
                                    transform={`rotate(${obraPortada(o).rotacion || 0}deg)`}
                                    transformOrigin="center"
                                  />
                                ) : null}
                              </Box>
                              <Box minW={0} flex={1}>
                                <Text fontWeight="600" fontSize="sm" lineClamp={2}>
                                  {o.titulo}
                                </Text>
                                {o.autor ? (
                                  <Text fontSize="xs" color="gray.600" lineClamp={1}>
                                    {o.autor}
                                  </Text>
                                ) : null}
                                <HStack gap={1} mt={1} flexWrap="wrap">
                                  <Badge fontSize="0.65rem">{o.estado}</Badge>
                                  {o.genero?.nombre ? (
                                    <Badge fontSize="0.65rem" variant="outline" colorPalette="purple">
                                      {o.genero.nombre}
                                    </Badge>
                                  ) : null}
                                  {(o.carpetasObra || []).map((co) => (
                                    <Badge
                                      key={co.carpeta?.id || co.id}
                                      fontSize="0.65rem"
                                      variant="subtle"
                                      colorPalette="cyan"
                                    >
                                      {co.carpeta?.nombre}
                                    </Badge>
                                  ))}
                                  <Text fontSize="xs" color="gray.500">
                                    {o._count?.paginas ?? 0} pág.
                                  </Text>
                                </HStack>
                              </Box>
                            </HStack>
                            <HStack gap={2} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="sm"
                                variant="outline"
                                colorPalette="purple"
                                onClick={() => openObraEdit(o.id)}><FaEdit />Editar
                                                              </Button>
                              <IconButton
                                size="sm"
                                variant="ghost"
                                colorPalette="red"
                                aria-label="Eliminar obra"
                                onClick={() => deleteObra(o.id)}><FaTrash /></IconButton>
                            </HStack>
                          </Flex>
                        ))}
                      </VStack>
                    ) : (
                      <VStack align="stretch" gap={2}>
                        <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                          <Text fontSize="xs" color="gray.500">
                            {obraPagination.total} obra{obraPagination.total === 1 ? '' : 's'}
                            {obraPagination.total > 0 ? ` · Mostrando ${obraPagination.start}-${obraPagination.end}` : ''}
                          </Text>
                          <HStack gap={2} flexWrap="wrap">
                            <HStack gap={1}>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={() => setObraPage((p) => Math.max(1, p - 1))}
                                disabled={obraPagination.page <= 1}
                              >
                                Anterior
                              </Button>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={() => setObraPage((p) => Math.min(obraPagination.totalPages, p + 1))}
                                disabled={obraPagination.page >= obraPagination.totalPages}
                              >
                                Siguiente
                              </Button>
                            </HStack>
                            <Text fontSize="xs" color="gray.500">
                              Página {obraPagination.page} / {obraPagination.totalPages}
                            </Text>
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                size="xs"
                                value={obraPageSize}
                                onChange={(e) => setObraPageSize(Number(e.target.value) || 25)}
                                w="120px">
                                <option value={10}>10 / pág.</option>
                                <option value={25}>25 / pág.</option>
                                <option value={50}>50 / pág.</option>
                                <option value={100}>100 / pág.</option>
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </HStack>
                        </Flex>
                        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4 }} gap={4}>
                          {pagedObras.map((o) => {
                            const port = obraPortada(o);
                            return (
                              <Card.Root
                                key={o.id}
                                variant="outline"
                                cursor="pointer"
                                borderColor="gray.200"
                                _hover={{ borderColor: 'purple.400', shadow: 'md' }}
                                transition="all 0.15s ease"
                                onClick={() => openObraFicha(o.id)}
                                overflow="hidden"
                              >
                                <AspectRatio ratio={3 / 4}>
                                  <Box bg="gray.100" position="relative">
                                    {port ? (
                                      <ChakraImage
                                        src={pageImageSrc(port)}
                                        alt={o.titulo}
                                        w="full"
                                        h="full"
                                        objectFit="cover"
                                        transform={`rotate(${port.rotacion || 0}deg)`}
                                        transformOrigin="center"
                                      />
                                    ) : (
                                      <Flex align="center" justify="center" h="full" bg="gray.100">
                                        <Icon fontSize="3xl" color="gray.400" asChild><FaMusic /></Icon>
                                      </Flex>
                                    )}
                                  </Box>
                                </AspectRatio>
                                <Card.Body py={3}>
                                  <Text fontWeight="600" fontSize="sm" lineClamp={2}>
                                    {o.titulo}
                                  </Text>
                                  {o.autor ? (
                                    <Text fontSize="xs" color="gray.600" lineClamp={1} mt={1}>
                                      {o.autor}
                                    </Text>
                                  ) : null}
                                  <HStack gap={1} mt={2} flexWrap="wrap">
                                    <Badge fontSize="0.65rem">{o.estado}</Badge>
                                    {o.genero?.nombre ? (
                                      <Badge fontSize="0.65rem" variant="outline" colorPalette="purple">
                                        {o.genero.nombre}
                                      </Badge>
                                    ) : null}
                                    {(o.carpetasObra || []).map((co) => (
                                      <Badge
                                        key={co.carpeta?.id || co.id}
                                        fontSize="0.65rem"
                                        variant="subtle"
                                        colorPalette="cyan"
                                      >
                                        {co.carpeta?.nombre}
                                      </Badge>
                                    ))}
                                    <Text fontSize="xs" color="gray.500">
                                      {o._count?.paginas ?? 0} pág.
                                    </Text>
                                  </HStack>
                                  <HStack gap={2} mt={3} onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      size="sm"
                                      flex={1}
                                      variant="outline"
                                      colorPalette="purple"
                                      onClick={() => openObraEdit(o.id)}><FaEdit />Editar
                                                                          </Button>
                                    <IconButton
                                      size="sm"
                                      variant="outline"
                                      colorPalette="red"
                                      aria-label="Eliminar obra"
                                      onClick={() => deleteObra(o.id)}><FaTrash /></IconButton>
                                  </HStack>
                                </Card.Body>
                              </Card.Root>
                            );
                          })}
                        </SimpleGrid>
                      </VStack>
                    )}
                    </Box>
                  </VStack>
                ) : obraViewMode === 'ficha' ? (
                  <BandaRepertorioObraFicha
                    detail={detail}
                    resumenPaginasEtiquetado={resumenPaginasEtiquetado}
                    paginasOrdenadas={paginasOrdenadasEditor}
                    nombreVozPorEtiqueta={nombreVozPorEtiqueta}
                    onOpenEtiquetado={abrirEtiquetadoObra}
                    onViewPage={abrirVisorPagina}
                    onDownloadPage={descargarPaginaObra}
                    descargandoPaginaId={descargandoPaginaId}
                    onDownloadObraCompleta={descargarObraCompleta}
                    descargandoObraCompleta={descargandoObraCompleta}
                    onAnadirPaginas={() => setAnadirPaginasWizardOpen(true)}
                    onPasarAListo={pasarObraAListo}
                    saving={saving}
                  />
                ) : (
                  <VStack align="stretch" gap={4}>
                    {(obraMetaDirty || isDirtyModal) ? (
                      <Alert.Root status="warning" variant="subtle" borderRadius="lg">
                        <Alert.Indicator />
                        <Box>
                          <Text fontWeight="800">Tienes cambios sin guardar</Text>
                          <Text fontSize="sm">
                            {obraMetaDirty ? 'Metadatos de la obra (título/autor/género/notas)' : null}
                            {obraMetaDirty && isDirtyModal ? ' y ' : null}
                            {isDirtyModal ? 'etiquetado de páginas' : null}
                            . Guarda para no perderlos.
                          </Text>
                        </Box>
                      </Alert.Root>
                    ) : null}
                    <Field.Root>
                      <Field.Label>Título obra</Field.Label>
                      <Input
                        value={detail.titulo || ''}
                        onChange={(e) => {
                          obraMetaDirtyRef.current = true;
                          setObraMetaDirty(true);
                          const v = e.target.value;
                          setDetail((prev) => (prev ? { ...prev, titulo: v } : prev));
                        }}
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Autor (obra)</Field.Label>
                      <Input
                        value={detail.autor || ''}
                        onChange={(e) => {
                          obraMetaDirtyRef.current = true;
                          setObraMetaDirty(true);
                          const v = e.target.value;
                          setDetail((prev) => (prev ? { ...prev, autor: v } : prev));
                        }}
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Género</Field.Label>
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        Maestro común (pasodoble, marcha procesional, etc.).
                      </Text>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          size="sm"
                          value={detail.generoId || ''}
                          onChange={(e) => {
                            obraMetaDirtyRef.current = true;
                            setObraMetaDirty(true);
                            const id = e.target.value || null;
                            const row = id ? generosCatalog.find((g) => g.id === id) : null;
                            setDetail((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    generoId: id,
                                    genero: row ? { id: row.id, codigo: row.codigo, nombre: row.nombre } : null,
                                  }
                                : prev,
                            );
                          }}>
                          <option value="">— Sin género —</option>
                          {detail.generoId &&
                            !generosCatalog.some((g) => g.id === detail.generoId) && (
                              <option value={detail.generoId}>
                                {detail.genero?.nombre || 'Género (actual)'} (actual)
                              </option>
                            )}
                          {generosCatalog.map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.nombre}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Notas del director (obra)</Field.Label>
                      <Textarea
                        value={detail.notasDirector || ''}
                        onChange={(e) => {
                          obraMetaDirtyRef.current = true;
                          setObraMetaDirty(true);
                          const v = e.target.value;
                          setDetail((prev) => (prev ? { ...prev, notasDirector: v } : prev));
                        }}
                        rows={2}
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Vídeo de YouTube</Field.Label>
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        Enlace opcional al vídeo de la obra. Aparecerá en el repertorio compartido junto al botón de
                        descarga.
                      </Text>
                      {detail.youtubeUrl ? (
                        <VStack align="stretch" gap={2}>
                          <HStack gap={2} align="center">
                            <Icon color="red.500" flexShrink={0} asChild><FaYoutube /></Icon>
                            <Link
                              href={detail.youtubeUrl}
                              color="purple.600"
                              fontSize="sm"
                              lineClamp={1}
                              flex={1}
                              target='_blank'
                              rel='noopener noreferrer'>
                              {detail.youtubeUrl}
                            </Link>
                          </HStack>
                          <HStack gap={2} flexWrap="wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={openYoutubeObraModal}
                              disabled={youtubeSaving}><FaYoutube />Cambiar vídeo
                                                          </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() => void quitarYoutubeObra()}
                              loading={youtubeSaving}
                            >
                              Quitar vídeo
                            </Button>
                          </HStack>
                        </VStack>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          colorPalette="red"
                          onClick={openYoutubeObraModal}
                          alignSelf="flex-start"><FaYoutube />Añadir vídeo de YouTube
                                                  </Button>
                      )}
                    </Field.Root>
                    <HStack
                      align="center"
                      gap={3}
                      p={3}
                      rounded="lg"
                      borderWidth="1px"
                      borderColor="purple.200"
                      bg="purple.50"
                      transition="background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease"
                      cursor={resumenPaginasEtiquetado.total > 0 ? 'pointer' : 'not-allowed'}
                      opacity={resumenPaginasEtiquetado.total > 0 ? 1 : 0.75}
                      userSelect="none"
                      aria-label={
                        resumenPaginasEtiquetado.total > 0
                          ? 'Abrir visor de páginas y etiquetado'
                          : 'Páginas y etiquetado: sin páginas aún'
                      }
                      _hover={
                        resumenPaginasEtiquetado.total > 0
                          ? {
                              bg: 'purple.100',
                              borderColor: 'purple.400',
                              boxShadow: '0 2px 10px rgba(107, 33, 168, 0.18)',
                              transform: 'translateY(-1px)',
                            }
                          : undefined
                      }
                      _active={
                        resumenPaginasEtiquetado.total > 0
                          ? { transform: 'translateY(0)', boxShadow: 'sm' }
                          : undefined
                      }
                      _dark={{
                        borderColor: 'purple.600',
                        bg: 'rgba(88, 28, 135, 0.22)',
                        _hover:
                          resumenPaginasEtiquetado.total > 0
                            ? {
                                bg: 'rgba(124, 58, 237, 0.38)',
                                borderColor: 'purple.400',
                                boxShadow: '0 0 0 1px rgba(196, 181, 253, 0.4), 0 4px 14px rgba(0,0,0,0.35)',
                                transform: 'translateY(-1px)',
                              }
                            : undefined,
                      }}
                      onClick={abrirEtiquetadoObra}
                      role="group"
                      title="Abrir visor de páginas y etiquetado"
                    >
                      <Flex
                        align="center"
                        justify="center"
                        w={10}
                        h={10}
                        rounded="md"
                        bg="white"
                        _dark={{ bg: 'gray.800' }}
                        borderWidth="1px"
                        borderColor="purple.300"
                        transition="background 0.18s ease, border-color 0.18s ease"
                        _groupHover={
                          resumenPaginasEtiquetado.total > 0
                            ? {
                                borderColor: 'purple.500',
                                bg: 'purple.50',
                                _dark: { bg: 'gray.700', borderColor: 'purple.300' },
                              }
                            : undefined
                        }
                      >
                        <Icon boxSize={5} color="purple.600" _dark={{ color: 'purple.300' }} asChild><FaLayerGroup /></Icon>
                      </Flex>
                      <Box flex="1" minW={0}>
                        <Text fontWeight="600" fontSize="sm" color="purple.900" _dark={{ color: 'purple.100' }}>
                          Páginas y etiquetado
                        </Text>
                        <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
                          {resumenPaginasEtiquetado.total === 0
                            ? 'Sin páginas subidas'
                            : `${resumenPaginasEtiquetado.total} página${
                                resumenPaginasEtiquetado.total === 1 ? '' : 's'
                              } · ${resumenPaginasEtiquetado.etiquetadas} etiquetada${
                                resumenPaginasEtiquetado.etiquetadas === 1 ? '' : 's'
                              }`}
                        </Text>
                      </Box>
                      <HStack
                        gap={1}
                        flexShrink={0}
                        color="purple.700"
                        opacity={resumenPaginasEtiquetado.total > 0 ? 1 : 0.45}
                        _dark={{ color: 'purple.200' }}
                        pointerEvents="none"
                      >
                        <Text fontSize="xs" fontWeight="700" letterSpacing="wide" textTransform="uppercase">
                          Abrir
                        </Text>
                        <Icon boxSize={4} aria-hidden asChild><FaChevronRight /></Icon>
                      </HStack>
                    </HStack>
                    <Button colorPalette="purple" size="sm" onClick={saveObraMeta} loading={saving}><FaSave />Guardar obra
                                          </Button>

                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Miniaturas, rotación, borrado y etiquetado (ámbito, instrumento, voces, notas) están en el visor
                      a pantalla completa: pulsa «Páginas y etiquetado» encima de «Guardar obra». Para añadir más
                      páginas usa «Añadir páginas» en la ficha o en el visor. «Guardar obra» guarda también el
                      etiquetado de todas las páginas; dentro del visor puedes usar «Guardar todas las páginas» sin
                      cerrar.
                    </Text>
                  </VStack>
                )}
              </Card.Body>
            </Card.Root>
              </TabPanel>
              <TabPanel value="1" px={0} pt={4}>
                <Card.Root bg={cardBg} shadow="md" rounded="2xl" w="full">
                  <Card.Header py={3}>
                    {selectedCarpeta ? (
                      <HStack gap={3} align="flex-start" flexWrap="wrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={carpetaViewMode === 'edit' ? () => setCarpetaViewMode('ficha') : goToCarpetasList}
                          flexShrink={0}><FaArrowLeft />{carpetaViewMode === 'edit' ? 'Volver a la ficha' : 'Volver al listado'}</Button>
                        <Box flex={1} minW={0}>
                          {carpetaViewMode === 'edit' ? (
                            <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" letterSpacing="wide">
                              Editar carpeta
                            </Text>
                          ) : null}
                          <Heading size="md" lineClamp={2}>
                            {selectedCarpeta.nombre}
                          </Heading>
                        </Box>
                        {carpetaViewMode === 'ficha' ? (
                          <HStack gap={2} flexShrink={0}>
                            <Button
                              size="sm"
                              colorPalette="purple"
                              variant="outline"
                              onClick={openCarpetaEdit}><FaEdit />Editar
                                                          </Button>
                            <Button
                              size="sm"
                              colorPalette="red"
                              variant="outline"
                              onClick={() => handleEliminarCarpeta(selectedCarpeta.id, selectedCarpeta.nombre)}
                              loading={carpetasMutating}><FaTrash />Eliminar
                                                          </Button>
                          </HStack>
                        ) : null}
                      </HStack>
                    ) : (
                      <VStack align="stretch" gap={1}>
                        <Heading size="md">Carpetas de partituras</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Las carpetas solo agrupan obras en tu repertorio. Eliminar una carpeta no borra ninguna obra.
                          Pulsa una carpeta para ver su ficha.
                        </Text>
                      </VStack>
                    )}
                  </Card.Header>
                  <Card.Body pt={0}>
                    {selectedCarpeta ? (
                      carpetaViewMode === 'ficha' ? (
                        <BandaRepertorioCarpetaFicha
                          carpeta={selectedCarpeta}
                          onGestionarObras={() => abrirDrawerObrasCarpeta(selectedCarpeta)}
                          onEliminarTodasObras={onDialogVaciarCarpetaOpen}
                          carpetasMutating={carpetasMutating}
                        />
                      ) : (
                        <VStack align="stretch" gap={4} maxW="640px">
                          <Field.Root>
                            <Field.Label>Nombre</Field.Label>
                            <Input
                              value={renombrarCarpetaTexto}
                              onChange={(e) => setRenombrarCarpetaTexto(e.target.value)}
                            />
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>Descripción</Field.Label>
                            <Textarea
                              rows={4}
                              value={renombrarCarpetaDescripcion}
                              onChange={(e) => setRenombrarCarpetaDescripcion(e.target.value)}
                              placeholder="Opcional"
                            />
                          </Field.Root>
                          <HStack gap={2}>
                            <Button
                              colorPalette="purple"
                              onClick={handleRenombrarCarpetaGuardar}
                              loading={carpetasMutating}><FaSave />Guardar cambios
                                                          </Button>
                            <Button variant="ghost" onClick={() => setCarpetaViewMode('ficha')}>
                              Cancelar
                            </Button>
                          </HStack>
                        </VStack>
                      )
                    ) : (
                    <>
                    <VStack align="stretch" gap={3} mb={6}>
                      <Grid templateColumns={{ base: '1fr', md: '1fr 240px 160px auto' }} gap={3} alignItems="flex-end">
                        <Field.Root>
                          <Field.Label fontSize="xs" mb={1}>
                            Buscar
                          </Field.Label>
                          <Input
                            size="sm"
                            placeholder="Nombre o descripción…"
                            value={carpetasSearch}
                            onChange={(e) => setCarpetasSearch(e.target.value)}
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label fontSize="xs" mb={1}>
                            Orden
                          </Field.Label>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={carpetasSort}
                              onChange={(e) => setCarpetasSort(e.target.value)}>
                              <option value="nombre_asc">Nombre (A → Z)</option>
                              <option value="nombre_desc">Nombre (Z → A)</option>
                              <option value="obras_desc">Nº de obras (mayor → menor)</option>
                              <option value="obras_asc">Nº de obras (menor → mayor)</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Field.Root>
                        <Field.Root>
                          <Field.Label fontSize="xs" mb={1}>
                            Por página
                          </Field.Label>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={carpetasPageSize}
                              onChange={(e) => setCarpetasPageSize(Number(e.target.value) || 10)}>
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Field.Root>
                        <Button
                          size="sm"
                          colorPalette="purple"
                          onClick={() => {
                            setNuevaCarpetaNombre('');
                            setNuevaCarpetaDescripcion('');
                            onModalCrearCarpetaOpen();
                          }}
                          loading={carpetasMutating}
                          flexShrink={0}><FaPlus />Nueva carpeta
                                                  </Button>
                      </Grid>

                      <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                        <Text fontSize="xs" color="gray.500">
                          {carpetasPagination.total} carpeta{carpetasPagination.total === 1 ? '' : 's'}
                          {carpetasPagination.total > 0
                            ? ` · Mostrando ${carpetasPagination.start}-${carpetasPagination.end}`
                            : ''}
                        </Text>
                        <HStack gap={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => setCarpetasPage((p) => Math.max(1, p - 1))}
                            disabled={carpetasPagination.page <= 1}
                          >
                            Anterior
                          </Button>
                          <Text fontSize="xs" color="gray.500">
                            Página {carpetasPagination.page} / {carpetasPagination.totalPages}
                          </Text>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => setCarpetasPage((p) => Math.min(carpetasPagination.totalPages, p + 1))}
                            disabled={carpetasPagination.page >= carpetasPagination.totalPages}
                          >
                            Siguiente
                          </Button>
                        </HStack>
                      </Flex>
                    </VStack>
                    <VStack align="stretch" gap={3}>
                      {filteredCarpetas.length === 0 ? (
                        <Text fontSize="sm" color="gray.500">
                          {carpetasCatalog.length === 0
                            ? 'Aún no hay carpetas.'
                            : 'Ninguna carpeta coincide con la búsqueda. Prueba a cambiar el texto o el orden.'}
                        </Text>
                      ) : (
                        pagedCarpetas.map((c) => (
                          <Flex
                            key={c.id}
                            borderWidth="1px"
                            rounded="lg"
                            p={3}
                            borderColor="gray.200"
                            cursor="pointer"
                            align="center"
                            justify="space-between"
                            gap={3}
                            _hover={{ borderColor: 'purple.300', bg: 'purple.50' }}
                            transition="colors 0.15s ease"
                            onClick={() => openCarpetaFicha(c)}
                          >
                            <Box minW={0} flex={1}>
                              <Text fontWeight="600" fontSize="sm">
                                {c.nombre}
                              </Text>
                              {c.descripcion ? (
                                <Text fontSize="xs" color="gray.600" lineClamp={2} whiteSpace="pre-wrap" mt={1}>
                                  {c.descripcion}
                                </Text>
                              ) : null}
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                {(c.obras || []).length} obra{(c.obras || []).length === 1 ? '' : 's'}
                              </Text>
                            </Box>
                            <IconButton
                              size="sm"
                              variant="ghost"
                              colorPalette="red"
                              aria-label="Eliminar carpeta"
                              flexShrink={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEliminarCarpeta(c.id, c.nombre);
                              }}
                              loading={carpetasMutating}><FaTrash /></IconButton>
                          </Flex>
                        ))
                      )}
                    </VStack>
                    </>
                    )}
                  </Card.Body>
                </Card.Root>
              </TabPanel>
              <TabPanel value="2" px={0} pt={4}>
                <Card.Root bg={cardBg} shadow="md" rounded="2xl" w="full" minW={0}>
                  <Card.Header py={3}>
                    <Heading size="md">Voces e instrumentos</Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      Catálogo de voces y de instrumentos de la banda: úsalos al etiquetar cada página. Las obras se crean
                      en la pestaña «Biblioteca».
                    </Text>
                  </Card.Header>
                  <Card.Body pt={0}>
                    <VStack align="stretch" gap={3}>
                      <Heading size="sm">Voces (toda la banda)</Heading>
                      <Text fontSize="xs" color="gray.600">
                        Mismas opciones en todas las obras. Añade las que uséis (1.ª, 2.ª, requinto, etc.).
                      </Text>
                      <HStack gap={2}>
                        <Input
                          size="sm"
                          placeholder="Ej. Corneta 2ª"
                          value={nuevaVozNombre}
                          onChange={(e) => setNuevaVozNombre(e.target.value)}
                          flex={1}
                          minW={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddVoz();
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleAddVoz}
                          loading={vocesMutating}
                          flexShrink={0}><FaPlus />Añadir
                                                  </Button>
                      </HStack>
                      {vocesCatalog.length > 0 ? (
                        <VStack align="stretch" gap={1} maxH={{ base: '160px', md: 'min(40vh, 320px)' }} overflowY="auto">
                          {vocesCatalog.map((v) => (
                            <HStack key={v.id} justify="space-between" fontSize="xs" py={0.5} gap={1}>
                              <Text
                                lineClamp={2}
                                color="gray.700"
                                title={`${v.nombre}${v.etiqueta ? ` · ${v.etiqueta}` : ''}`}
                              >
                                {v.nombre}
                                {v.etiqueta ? ` · ${v.etiqueta}` : ''}
                              </Text>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorPalette="red"
                                flexShrink={0}
                                aria-label="Eliminar voz"
                                onClick={() => handleDeleteVoz(v.id, v.nombre)}
                                disabled={vocesMutating}
                              >
                                <FaTrash />
                              </Button>
                            </HStack>
                          ))}
                        </VStack>
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          Aún no hay voces en el catálogo de la banda.
                        </Text>
                      )}
                      <Separator />
                      <Heading size="sm">Instrumentos (catálogo y personalizados)</Heading>
                      <Text fontSize="xs" color="gray.600">
                        El catálogo depende del tipo de agrupación de la banda; los personalizados son solo tuyos. Todos
                        aparecen aquí, al etiquetar páginas y al generar partichellas.
                      </Text>
                      {instrumentosParaEtiquetado.length > 0 ? (
                        <Stack gap={1} maxH="280px" overflowY="auto" fontSize="sm">
                          {instrumentosParaEtiquetado.map((i) => (
                            <HStack
                              key={i.esPersonalizado ? `p-${i.id}` : `c-${i.id}`}
                              justify="space-between"
                              align="flex-start"
                              gap={2}
                            >
                              <Text lineClamp={2} flex={1}>
                                {i.nombre}
                              </Text>
                              <HStack flexShrink={0} gap={1} align="center">
                                {i.esPersonalizado ? (
                                  <Badge fontSize="0.6rem" colorPalette="purple" variant="subtle">
                                    Personalizado
                                  </Badge>
                                ) : null}
                                <Badge fontSize="0.65rem">
                                  {FAMILIA_INSTRUMENTO_LABELS[i.familia] || i.familia}
                                </Badge>
                                {i.esPersonalizado ? (
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="red"
                                    aria-label="Eliminar instrumento personalizado"
                                    onClick={() => handleEliminarInstrumentoPersonalizado(i.id, i.nombre)}
                                    loading={instPersMutating}><FaTrash /></IconButton>
                                ) : null}
                              </HStack>
                            </HStack>
                          ))}
                        </Stack>
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          No hay instrumentos cargados o aún se están cargando.
                        </Text>
                      )}
                      <Separator />
                      <Heading size="sm">Nuevo instrumento personalizado</Heading>
                      <Text fontSize="xs" color="gray.600">
                        Añade un nombre que no esté ya en el catálogo; aparecerá en la lista de arriba con la etiqueta
                        «Personalizado».
                      </Text>
                      <HStack gap={2} flexWrap="wrap" align="flex-end">
                        <Field.Root maxW="280px" flex={1} minW={0}>
                          <Field.Label fontSize="xs" mb={1}>
                            Nombre
                          </Field.Label>
                          <Input
                            size="sm"
                            value={nuevoInstrumentoNombre}
                            onChange={(e) => setNuevoInstrumentoNombre(e.target.value)}
                            placeholder="Ej. Corneta bajo"
                          />
                        </Field.Root>
                        <Field.Root maxW="200px" w={{ base: 'full', sm: '200px' }}>
                          <Field.Label fontSize="xs" mb={1}>
                            Familia
                          </Field.Label>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={nuevoInstrumentoFamilia}
                              onChange={(e) => setNuevoInstrumentoFamilia(e.target.value)}>
                              {Object.entries(FAMILIA_INSTRUMENTO_LABELS).map(([k, lab]) => (
                                <option key={k} value={k}>
                                  {lab}
                                </option>
                              ))}
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Field.Root>
                        <Button
                          size="sm"
                          colorPalette="purple"
                          onClick={handleCrearInstrumentoPersonalizado}
                          loading={instPersMutating}
                          flexShrink={0}><FaPlus />Añadir
                                                  </Button>
                      </HStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              </TabPanel>
              <TabPanel value="3" px={0} pt={4}>
                <Card.Root bg={cardBg} shadow="md" rounded="2xl" w="full">
                  <Card.Header py={3}>
                    <Heading size="md">Compartir repertorio</Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      Puedes publicar un acceso de solo lectura a las partituras. Esta es la forma de compartir de forma
                      general el enlace para todo el grupo: un mismo enlace y la misma contraseña para todos. El enlace
                      público solo aparece en esta pestaña después de definir una contraseña de acceso; quien reciba el
                      enlace necesitará esa clave.
                    </Text>
                    <Tabs.Root
                      value={tabsControlledValue(compartirSubTabIndex)}
                      onValueChange={tabsOnValueChange((idx) => {
                        setCompartirSubTabIndex(idx);
                        const next = new URLSearchParams(searchParams);
                        if (idx === 1) next.set('compartir', 'incidencias');
                        else next.delete('compartir');
                        setSearchParams(next, { replace: true });
                      })}
                      variant='enclosed'
                      colorPalette="purple"
                      size="sm"
                      mt={4}
                    >
                      <Tabs.List>
                        <Tab value="0" fontWeight="600">Enlace y músicos</Tab>
                        <Tab value="1" fontWeight="600">Incidencias</Tab>
                      </Tabs.List>
                    </Tabs.Root>
                  </Card.Header>
                  <Card.Body pt={0}>
                    {compartirSubTabIndex === 1 ? (
                      <BandaRepertorioShareIncidencias active={archivoMusicaTabIndex === 3} />
                    ) : (
                    <>
                    <VStack align="stretch" gap={3}>
                      <Alert.Root
                        status="warning"
                        variant='subtle'
                        fontSize="sm"
                        borderRadius="md"
                        borderStartWidth='3px'
                        borderStartColor='colorPalette.solid'>
                        <Alert.Indicator />
                        <Box>
                          <Text fontWeight="medium">Solo obras en estado «Listo»</Text>
                          <Text mt={1} color="gray.600">
                            En el repertorio compartido solo se muestran las obras que hayas marcado como listas. Las que
                            sigan en borrador o procesándose no aparecerán en ese enlace hasta que las pases a listo desde
                            cada obra.
                          </Text>
                        </Box>
                      </Alert.Root>
                      {repertorioShareLoading ? (
                        <Text fontSize="sm" color="gray.500">
                          Cargando…
                        </Text>
                      ) : repertorioShare.hasPassword ? (
                        <Field.Root>
                          <Field.Label fontSize="xs" mb={1}>
                            Enlace (solo lectura)
                          </Field.Label>
                          <HStack gap={2} align="flex-start">
                            <Input
                              size="sm"
                              value={repertorioShare.shareUrl}
                              readOnly
                              flex={1}
                              minW={0}
                              bg="gray.50"
                              _dark={{ bg: 'whiteAlpha.100' }}
                              cursor="default"
                            />
                            <IconButton
                              aria-label="Copiar enlace"
                              size="sm"
                              variant="outline"
                              onClick={copyRepertorioShareUrl}
                              disabled={!repertorioShare.shareUrl}><FaCopy /></IconButton>
                            <Button
                              size="sm"
                              variant="outline"
                              colorPalette="whatsapp"
                              onClick={shareRepertorioPorWhatsApp}
                              disabled={!repertorioShare.shareUrl}
                              flexShrink={0}><FaWhatsapp />WhatsApp
                                                          </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              colorPalette="purple"
                              onClick={abrirDialogoGenerarQr}
                              loading={generandoQrRepertorio}
                              disabled={!repertorioShare.shareUrl || generandoQrRepertorio}
                              flexShrink={0}><FaQrcode />Generar QR
                                                          </Button>
                            {repertorioShare.shareUrl ? (
                              <Button isExternal size="sm" variant="outline" flexShrink={0} asChild><Link href={repertorioShare.shareUrl}>Abrir
                                                              </Link></Button>
                            ) : null}
                          </HStack>
                        </Field.Root>
                      ) : (
                        <Alert.Root
                          status="info"
                          variant='subtle'
                          fontSize="sm"
                          borderRadius="md"
                          borderStartWidth='3px'
                          borderStartColor='colorPalette.solid'>
                          <Alert.Indicator />
                          <Box>
                            <Text fontWeight="medium">Aún no hay enlace activo</Text>
                            <Text mt={1} color="gray.600">
                              {isTitularBanda
                                ? 'Pulsa el botón «Compartir repertorio» abajo, elige una contraseña de acceso y, al guardarla, se mostrará el enlace para copiarlo o abrirlo.'
                                : 'Cuando el titular de la cuenta active la opción y defina una contraseña, aquí aparecerá el enlace para consultar el repertorio compartido.'}
                            </Text>
                          </Box>
                        </Alert.Root>
                      )}
                      <HStack flexWrap="wrap" gap={2}>
                        {isTitularBanda && !repertorioShare.hasPassword && !repertorioShareLoading ? (
                          <Button colorPalette="purple" size="sm" onClick={openCompartirRepertorioModal}><FaShareAlt />Compartir repertorio
                                                      </Button>
                        ) : null}
                        {isTitularBanda && repertorioShare.hasPassword ? (
                          <>
                            <Button variant="outline" size="sm" onClick={openVerPwdModal}><FaEye />Ver contraseña
                                                          </Button>
                            <Button variant="outline" size="sm" onClick={openCambiarPwdModal}><FaKey />Cambiar contraseña
                                                          </Button>
                          </>
                        ) : null}
                      </HStack>
                      {!isTitularBanda ? (
                        <Text fontSize="xs" color="gray.500">
                          Solo el titular de la cuenta (usuario principal de la banda) puede activar el enlace y definir o
                          cambiar la contraseña.
                        </Text>
                      ) : repertorioShare.hasPassword ? (
                        <Text fontSize="xs" color="gray.500">
                          El enlace no cambia si actualizas la contraseña; solo cambia la clave que deben usar quienes
                          acceden.
                        </Text>
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          La contraseña no se guarda en texto plano: apúntala en un lugar seguro al activar el enlace.
                        </Text>
                      )}
                    </VStack>
                    {repertorioShare.hasPassword && !repertorioShareLoading ? (
                      <>
                        <Separator my={5} />
                        <Heading size="sm">Compartir de forma individual</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Cada músico de la plantilla tiene un enlace único (añade músicos en el perfil de la banda →
                          plantilla). Tras copiar o enviar por WhatsApp queda registrada la fecha de último envío.
                        </Text>
                        {musicoEnlacesLoading ? (
                          <Flex justify="center" py={4}>
                            <Spinner size="sm" color="purple.500" />
                          </Flex>
                        ) : musicoEnlaces.enabled && musicoEnlaces.musicos.length > 0 ? (
                          <Box overflowX="auto">
                            <Table.Root size="sm" variant="simple">
                              <Table.Header>
                                <Table.Row>
                                  <Table.ColumnHeader>Músico</Table.ColumnHeader>
                                  <Table.ColumnHeader>Instrumentos y voces</Table.ColumnHeader>
                                  <Table.ColumnHeader>Teléfono</Table.ColumnHeader>
                                  <Table.ColumnHeader>Última vez compartido</Table.ColumnHeader>
                                  <Table.ColumnHeader textAlign="right">Acciones</Table.ColumnHeader>
                                </Table.Row>
                              </Table.Header>
                              <Table.Body>
                                {musicoEnlaces.musicos.map((m) => (
                                  <Table.Row key={m.id} opacity={m.activo ? 1 : 0.55}>
                                    <Table.Cell>
                                      <Text fontWeight="600" fontSize="sm">
                                        {m.nombre} {m.apellidos || ''}
                                      </Text>
                                      {!m.activo ? (
                                        <Badge fontSize="0.65rem" colorPalette="gray" mt={1}>
                                          Inactivo
                                        </Badge>
                                      ) : null}
                                    </Table.Cell>
                                    <Table.Cell fontSize="sm" color="gray.600" maxW="280px">
                                      <Text lineClamp={4} title={formatMusicoInstrumentosText(m, ', ')}>
                                        {formatMusicoInstrumentosText(m, ' · ')}
                                      </Text>
                                    </Table.Cell>
                                    <Table.Cell fontSize="sm" color="gray.600">
                                      {m.telefono?.trim() ? m.telefono : '—'}
                                    </Table.Cell>
                                    <Table.Cell fontSize="sm" color="gray.600" whiteSpace="nowrap">
                                      {formatUltimoCompartido(m.repertorioUltimoCompartidoAt)}
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                      <HStack gap={1} justify="flex-end" flexWrap="wrap">
                                        <IconButton
                                          aria-label="Copiar enlace individual"
                                          size="sm"
                                          variant="outline"
                                          onClick={() => copyEnlaceIndividualMusico(m)}><FaCopy /></IconButton>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          colorPalette="whatsapp"
                                          onClick={() => shareWhatsappIndividualMusico(m)}><FaWhatsapp />WhatsApp
                                                                                  </Button>
                                      </HStack>
                                    </Table.Cell>
                                  </Table.Row>
                                ))}
                              </Table.Body>
                            </Table.Root>
                          </Box>
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            {musicoEnlaces.enabled
                              ? 'Aún no hay músicos en la plantilla. Añádelos desde el perfil de la banda (sección músicos).'
                              : 'Activa primero la contraseña del enlace general arriba para generar enlaces individuales.'}
                          </Text>
                        )}
                      </>
                    ) : null}
                    </>
                    )}
                  </Card.Body>
                </Card.Root>
              </TabPanel>
              <TabPanel value="4" px={0} pt={4}>
                <BandaRepertorioEstadisticas active={archivoMusicaTabIndex === 4} />
              </TabPanel>
            </TabPanels>
          </Tabs.Root>
        </VStack>
      </Container>

      <BandaRepertorioAltaObraWizard
        isOpen={altaObraWizardOpen}
        generosCatalog={generosCatalog}
        detectarNuevosMaestros={detectarNuevosMaestrosEnEtiquetado}
        crearMaestrosPendientes={crearMaestrosPendientes}
        onClose={() => {
          setAltaObraWizardOpen(false);
        }}
        onComplete={() => {
          setAltaObraWizardOpen(false);
          void handleAltaObrasCompletadas();
        }}
      />

      <BandaRepertorioAnadirPaginasWizard
        isOpen={anadirPaginasWizardOpen}
        obraId={detail?.id || selectedId}
        obraTitulo={detail?.titulo}
        onClose={() => setAnadirPaginasWizardOpen(false)}
        onComplete={async ({ openEtiquetado }) => {
          setAnadirPaginasWizardOpen(false);
          const oid = detail?.id || selectedId;
          if (oid) {
            await loadDetail(oid);
            await loadList();
          }
          if (openEtiquetado) {
            onModalEtiquetadoPaginasOpen();
          }
        }}
      />

      {detail ? (
        <BandaRepertorioEtiquetadoPaginasModal
          isOpen={modalEtiquetadoPaginasOpen}
          onClose={onModalEtiquetadoPaginasClose}
          isDirtyModal={isDirtyModal}
          obraTitulo={detail.titulo}
          detail={detail}
          setDetail={setDetail}
          selectedPage={selectedPage}
          selectedPageId={selectedPageId}
          setSelectedPageId={setSelectedPageId}
          paginasOrdenadasEditor={paginasOrdenadasEditor}
          ambitoPagina={ambitoPagina}
          setAmbitoPagina={setAmbitoPagina}
          rangoOrdenesTexto={rangoOrdenesTexto}
          setRangoOrdenesTexto={setRangoOrdenesTexto}
          rangoPatch={rangoPatch}
          setRangoPatch={setRangoPatch}
          instrumentCatalog={instrumentosParaEtiquetado}
          vocesCatalog={vocesCatalog}
          saving={saving}
          nombreVozPorEtiqueta={nombreVozPorEtiqueta}
          slugVozFila={slugVozFila}
          initRangoDesdePagina={initRangoDesdePagina}
          savePage={savePage}
          saveAllPagesEtiquetas={saveAllPagesEtiquetas}
          rotatePage={rotatePage}
          rotateAllPages90={rotateAllPages90}
          deletePage={deletePage}
          aplicarEtiquetasVariosTramos={aplicarEtiquetasVariosTramos}
          setVisorPartituraAbierto={(open) => {
            if (open) {
              if (selectedPageId) abrirVisorPagina(selectedPageId);
            } else {
              cerrarVisorPartitura();
            }
          }}
          bulkActionStatus={bulkActionStatus}
          crearVozMaestro={crearVozMaestroDesdeEtiquetado}
          crearInstrumentoMaestro={crearInstrumentoMaestroDesdeEtiquetado}
          onAnadirPaginas={() => setAnadirPaginasWizardOpen(true)}
        />
      ) : null}

      <Dialog.Root open={isObrasExportOpen} placement='center' size='sm' onOpenChange={e => {
        if (!e.open) {
          closeObrasExportModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="md" py={3}>
                {obrasExportStep === 'pdf-orden'
                  ? 'Orden del listado'
                  : obrasExportStep === 'pdf-tipo'
                    ? 'Tipo de PDF'
                    : 'Descargar listado'}
              </Dialog.Header>
              <Dialog.CloseTrigger disabled={obrasExportLoading} size="sm" />
              <Dialog.Body pb={4} pt={0}>
                {obrasExportStep === 'format' ? (
                  <>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      Se exportarán todas las obras del archivo musical ({obras.length} obra
                      {obras.length === 1 ? '' : 's'}).
                    </Text>
                    <SimpleGrid columns={2} gap={3}>
                      <Button
                        size="sm"
                        h="auto"
                        py={3}
                        px={2}
                        flexDirection="column"
                        variant="outline"
                        colorPalette="red"
                        onClick={() => setObrasExportStep('pdf-tipo')}
                        disabled={obrasExportLoading}
                      >
                        <Icon boxSize={4} mb={1} asChild><FaFilePdf /></Icon>
                        PDF
                        <Text fontSize="2xs" fontWeight="normal" color="gray.500" mt={0.5} lineHeight="short">
                          Con cabecera Cadencya
                        </Text>
                      </Button>
                      <Button
                        size="sm"
                        h="auto"
                        py={3}
                        px={2}
                        flexDirection="column"
                        variant="outline"
                        colorPalette="green"
                        onClick={handleExportObrasExcel}
                        loading={obrasExportLoading}
                        loadingText="Generando…"
                      >
                        <Icon boxSize={4} mb={1} asChild><FaFileExcel /></Icon>
                        Excel
                        <Text fontSize="2xs" fontWeight="normal" color="gray.500" mt={0.5} lineHeight="short">
                          Hoja de cálculo .xlsx
                        </Text>
                      </Button>
                    </SimpleGrid>
                  </>
                ) : obrasExportStep === 'pdf-tipo' ? (
                  <>
                    <Button
                      size="xs"
                      variant="ghost"
                      mb={2}
                      onClick={() => setObrasExportStep('format')}
                      disabled={obrasExportLoading}><FaArrowLeft />Volver
                                      </Button>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      Elige el formato del documento PDF.
                    </Text>
                    <SimpleGrid columns={2} gap={3}>
                      <Button
                        size="sm"
                        h="auto"
                        py={3}
                        px={2}
                        flexDirection="column"
                        variant="outline"
                        colorPalette="purple"
                        onClick={() => {
                          setObrasExportPdfMode('gestion');
                          setObrasExportStep('pdf-orden');
                        }}
                        disabled={obrasExportLoading}
                      >
                        <Icon boxSize={4} mb={1} asChild><FaList /></Icon>
                        Gestión
                        <Text fontSize="2xs" fontWeight="normal" color="gray.500" mt={0.5} lineHeight="short">
                          Toda la información
                        </Text>
                      </Button>
                      <Button
                        size="sm"
                        h="auto"
                        py={3}
                        px={2}
                        flexDirection="column"
                        variant="outline"
                        colorPalette="purple"
                        onClick={() => {
                          setObrasExportPdfMode('exhibicion');
                          setObrasExportStep('pdf-orden');
                        }}
                        disabled={obrasExportLoading}
                      >
                        <Icon boxSize={4} mb={1} asChild><FaEye /></Icon>
                        Exhibición
                        <Text fontSize="2xs" fontWeight="normal" color="gray.500" mt={0.5} lineHeight="short">
                          Título y autor + logo en cabecera
                        </Text>
                      </Button>
                    </SimpleGrid>
                  </>
                ) : (
                  <>
                    <Button
                      size="xs"
                      variant="ghost"
                      mb={2}
                      onClick={() => setObrasExportStep('pdf-tipo')}
                      disabled={obrasExportLoading}><FaArrowLeft />Volver
                                      </Button>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      ¿Cómo quieres ordenar las obras en el PDF?
                    </Text>
                    <RadioGroup.Root
                      value={obrasExportOrden}
                      onValueChange={(d) => setObrasExportOrden(d.value)}
                      mb={4}
                      disabled={obrasExportLoading}>
                      <Stack gap={2}>
                        <RadioGroup.Item value="alfabetico" size="sm"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Alfabético (por título)
                                                    </RadioGroup.ItemText></RadioGroup.Item>
                        <RadioGroup.Item value="genero" size="sm"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Por género (y título dentro de cada género)
                                                    </RadioGroup.ItemText></RadioGroup.Item>
                      </Stack>
                    </RadioGroup.Root>
                    <Button
                      size="sm"
                      w="full"
                      colorPalette="purple"
                      onClick={() => handleExportObrasPdf(obrasExportPdfMode, obrasExportOrden)}
                      loading={obrasExportLoading}
                      loadingText="Generando…"
                      disabled={!obrasExportPdfMode}><FaDownload />Descargar PDF
                                      </Button>
                  </>
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={modalPartichellasAbierto}
        size='lg'
        onOpenChange={e => {
          if (!e.open) {
            setModalPartichellasAbierto(false);
            setPartichellasInstrumentSlug('');
            setPartichellasVocesPreview([]);
            setPartichellasVocesSel([]);
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Descargar partichellas (PDF único)</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Alert.Root
                  status="warning"
                  rounded="md"
                  variant='subtle'
                  mb={4}
                  borderStartWidth='3px'
                  borderStartColor='colorPalette.solid'>
                  <Alert.Indicator />
                  <Box>
                    <Text fontWeight="700" fontSize="sm">
                      Se descargarán solo las obras del filtro actual
                    </Text>
                    <Text fontSize="sm">
                      Ahora mismo: {partichellaObraIdsOrdenados.length} obra
                      {partichellaObraIdsOrdenados.length === 1 ? '' : 's'} visibles. No es todo el archivo: si
                      quieres una carpeta o un género concreto, filtra primero y luego descarga.
                    </Text>
                  </Box>
                </Alert.Root>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  Puedes elegir un instrumento o «Todas las páginas» para unir todas las páginas de cada obra. Si
                  filtras por carpeta, el orden en el PDF es el de programa de esa carpeta; si no, el del listado
                  actual.
                </Text>
                <Field.Root mb={4}>
                  <Field.Label fontSize="sm">Instrumento</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      size="sm"
                      placeholder="Elige instrumento o todas las páginas…"
                      value={partichellasInstrumentSlug}
                      onChange={(e) => setPartichellasInstrumentSlug(e.target.value)}>
                      <option value={PARTICHELLAS_INSTR_TODAS}>Todas las páginas (cada obra completa)</option>
                      {instrumentosParaEtiquetado.map((i) => {
                        const slug = etiquetaSlugDesdeNombre(i.nombre);
                        return (
                          <option key={i.esPersonalizado ? `p-${i.id}` : i.id} value={slug}>
                            {i.nombre}
                            {i.esPersonalizado ? ' (personalizado)' : ''}
                          </option>
                        );
                      })}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
                {partichellasInstrumentSlug === PARTICHELLAS_INSTR_TODAS ? (
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    No se filtra por instrumento ni por voz: se exportan todas las páginas de cada obra, en el orden
                    indicado arriba.
                  </Text>
                ) : partichellasPreviewLoading ? (
                  <Flex py={4} justify="center">
                    <Spinner size="sm" color="purple.500" />
                  </Flex>
                ) : partichellasVocesPreview.length > 1 ? (
                  <MultiCheckboxField mb={2} label="Voces (opcional)">
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Si no marcas ninguna, se incluyen todas las variantes de voz encontradas para ese instrumento en
                      estas obras. Si marcas una o varias, se incluyen esas voces y, además, las páginas del mismo
                      instrumento sin voz etiquetada (p. ej. «Flauta» al descargar «Flauta 1»).
                    </Text>
                    <CheckboxGroup value={partichellasVocesSel} onValueChange={setPartichellasVocesSel}>
                      <Stack gap={2} maxH="200px" overflowY="auto">
                        {partichellasVocesPreview.map((v) => (
                          <Checkbox.Root key={v} value={v} size="sm"><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                            {nombreVozPorEtiqueta(v) || v}
                          </Checkbox.Label></Checkbox.Root>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  </MultiCheckboxField>
                ) : partichellasInstrumentSlug &&
                  partichellasInstrumentSlug !== PARTICHELLAS_INSTR_TODAS &&
                  partichellasVocesPreview.length === 1 ? (
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Solo hay una voz distinta ({nombreVozPorEtiqueta(partichellasVocesPreview[0]) || partichellasVocesPreview[0]}) en
                    estas páginas; se incluirá automáticamente.
                  </Text>
                ) : partichellasInstrumentSlug &&
                  partichellasInstrumentSlug !== PARTICHELLAS_INSTR_TODAS &&
                  partichellasVocesPreview.length === 0 ? (
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    No hay voces etiquetadas en las páginas de este instrumento (o aún no hay páginas); se tomarán todas las
                    páginas del instrumento sin filtrar por voz.
                  </Text>
                ) : null}
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setModalPartichellasAbierto(false);
                    setPartichellasInstrumentSlug('');
                    setPartichellasVocesPreview([]);
                    setPartichellasVocesSel([]);
                  }}
                >
                  Cerrar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleDescargarPartichellasPdf}
                  loading={partichellasPdfLoading}
                  disabled={!partichellasInstrumentSlug || partichellaObraIdsOrdenados.length === 0}><FaDownload />Descargar PDF
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <BandaRepertorioCarpetaObrasModal
        isOpen={Boolean(drawerCarpetaObras)}
        carpeta={drawerCarpetaObras}
        onClose={cerrarGestionObrasCarpeta}
        obras={obras}
        generosCatalog={generosCatalog}
        ordenIds={carpetaDrawerOrdenIds}
        onToggleObra={toggleObraEnCarpetaDrawer}
        onDragEnd={onDragEndCarpetaPrograma}
        search={carpetaDrawerSearch}
        onSearchChange={setCarpetaDrawerSearch}
        generoId={carpetaDrawerGeneroId}
        onGeneroIdChange={setCarpetaDrawerGeneroId}
        estado={carpetaDrawerEstado}
        onEstadoChange={setCarpetaDrawerEstado}
        filteredObras={filteredObrasCarpetaDrawer}
        onSave={handleGuardarObrasEnCarpeta}
        saving={carpetasMutating}
      />

      <Dialog.Root
        open={modalShareOpen}
        placement='center'
        size='md'
        onOpenChange={e => {
          if (!e.open) {
            setSharePwd('');
            setSharePwd2('');
            onModalShareClose();
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Compartir repertorio</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack align="stretch" gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Elige una contraseña que compartirás solo con quien quieras que consulte las partituras. Al guardarla,
                    se mostrará el enlace público en esta misma sección. Más adelante podrás cambiar la contraseña sin que
                    cambie la URL.
                  </Text>
                  <Alert.Root
                    status="warning"
                    variant='subtle'
                    fontSize="sm"
                    borderRadius="md"
                    borderStartWidth='3px'
                    borderStartColor='colorPalette.solid'>
                    <Alert.Indicator />
                    <Box>
                      No uses la misma contraseña que la de tu cuenta Cadencya. Si coinciden, quien obtenga la clave del
                      enlace podría intentar acceder a tu cuenta.
                    </Box>
                  </Alert.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Contraseña de acceso</Field.Label>
                    <Input
                      type="password"
                      value={sharePwd}
                      onChange={(e) => setSharePwd(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      autoComplete="new-password"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Repetir contraseña</Field.Label>
                    <Input
                      type="password"
                      value={sharePwd2}
                      onChange={(e) => setSharePwd2(e.target.value)}
                      placeholder="Repite la contraseña"
                      autoComplete="new-password"
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSharePwd('');
                    setSharePwd2('');
                    onModalShareClose();
                  }}
                >
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={submitRepertorioSharePassword} loading={sharePwdSaving}>
                  Guardar y mostrar enlace
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={dialogQrIncluirPwdOpen}
        initialFocusEl={() => cancelQrIncluirPwdRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDialogQrIncluirPwdClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Generar QR
                </Dialog.Header>
                <Dialog.Body>
                  ¿Deseas incluir la contraseña del enlace en la imagen del QR? Si la incluyes, quien escanee el código
                  verá también la clave de acceso al repertorio.
                </Dialog.Body>
                <Dialog.Footer gap={2}>
                  <Button ref={cancelQrIncluirPwdRef} onClick={onQrIncluirPwdNo}>
                    No, solo el QR
                  </Button>
                  <Button colorPalette="purple" onClick={onQrIncluirPwdSi}>
                    Sí, incluir contraseña
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <Dialog.Root open={modalYoutubeObraOpen} placement='center' size='md' onOpenChange={e => {
        if (!e.open) {
          closeYoutubeObraModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>{detail?.youtubeUrl ? 'Cambiar vídeo de YouTube' : 'Añadir vídeo de YouTube'}</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack align="stretch" gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Pega la URL del vídeo (youtube.com, youtu.be o Shorts). Se mostrará en el repertorio compartido.
                  </Text>
                  <Field.Root>
                    <Field.Label fontSize="sm">URL de YouTube</Field.Label>
                    <Input
                      value={youtubeUrlDraft}
                      onChange={(e) => setYoutubeUrlDraft(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void guardarYoutubeObra();
                      }}
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button variant="ghost" onClick={closeYoutubeObraModal}>
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={() => void guardarYoutubeObra()} loading={youtubeSaving}>
                  Guardar vídeo
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={modalQrPwdOpen} placement='center' size='md' onOpenChange={e => {
        if (!e.open) {
          closeQrPwdModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Contraseña para el QR</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack align="stretch" gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Por seguridad, indica la contraseña con la que inicias sesión en Cadencya. Si es correcta, generaremos
                    el QR con la contraseña del enlace visible debajo del título.
                  </Text>
                  <Field.Root>
                    <Field.Label fontSize="sm">Contraseña de tu cuenta Cadencya</Field.Label>
                    <Input
                      type="password"
                      value={qrPwdAccount}
                      onChange={(e) => setQrPwdAccount(e.target.value)}
                      autoComplete="current-password"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void submitQrRevealAndGenerate();
                      }}
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button ref={cancelQrPwdRef} variant="ghost" onClick={closeQrPwdModal}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={() => void submitQrRevealAndGenerate()}
                  loading={qrPwdLoading || generandoQrRepertorio}
                >
                  Generar QR
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={modalVerPwdOpen} placement='center' size='md' onOpenChange={e => {
        if (!e.open) {
          closeVerPwdModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Contraseña del enlace</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {!repertorioShare.canRevealSharePassword ? (
                  <Text fontSize="sm" color="gray.600">
                    Esta clave del enlace se guardó antes de poder mostrarla de forma segura. Cambia la contraseña del enlace
                    una vez con «Cambiar contraseña»; a partir de entonces podrás consultarla aquí tras indicar tu contraseña
                    de cuenta Cadencya.
                  </Text>
                ) : verPwdRevealed ? (
                  <VStack align="stretch" gap={3}>
                    <Text fontSize="sm" color="gray.600">
                      Contraseña que deben introducir quienes abren el enlace público (no tu contraseña de acceso a
                      Cadencya).
                    </Text>
                    <HStack gap={2}>
                      <Input
                        size="sm"
                        value={verPwdRevealed}
                        readOnly
                        fontFamily="mono"
                        bg="gray.50"
                        _dark={{ bg: 'whiteAlpha.100' }}
                      />
                      <IconButton
                        aria-label="Copiar contraseña del enlace"
                        size="sm"
                        variant="outline"
                        onClick={copyRevealedSharePassword}><FaCopy /></IconButton>
                    </HStack>
                  </VStack>
                ) : (
                  <VStack align="stretch" gap={4}>
                    <Text fontSize="sm" color="gray.600">
                      Por seguridad, indica la contraseña con la que inicias sesión en Cadencya (cuenta de la banda). Si es
                      correcta, te mostraremos la contraseña del enlace de repertorio.
                    </Text>
                    <Field.Root>
                      <Field.Label fontSize="sm">Contraseña de tu cuenta Cadencya</Field.Label>
                      <Input
                        type="password"
                        value={verPwdAccount}
                        onChange={(e) => setVerPwdAccount(e.target.value)}
                        autoComplete="current-password"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitRevealRepertorioSharePassword();
                        }}
                      />
                    </Field.Root>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                {!repertorioShare.canRevealSharePassword ? (
                  <Button onClick={closeVerPwdModal}>Entendido</Button>
                ) : verPwdRevealed ? (
                  <Button colorPalette="purple" onClick={closeVerPwdModal}>
                    Cerrar
                  </Button>
                ) : (
                  <>
                    <Button variant="ghost" onClick={closeVerPwdModal}>
                      Cancelar
                    </Button>
                    <Button colorPalette="purple" onClick={submitRevealRepertorioSharePassword} loading={verPwdLoading}>
                      Ver contraseña del enlace
                    </Button>
                  </>
                )}
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={modalCrearCarpetaOpen}
        placement='center'
        size='lg'
        onOpenChange={e => {
          if (!e.open) {
            onModalCrearCarpetaClose();
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Nueva carpeta</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack align="stretch" gap={4}>
                  <Field.Root>
                    <Field.Label fontSize="sm">Nombre</Field.Label>
                    <Input
                      placeholder="Ej. Concierto Mayo Romería"
                      value={nuevaCarpetaNombre}
                      onChange={(e) => setNuevaCarpetaNombre(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleCrearCarpetaFromModal();
                        }
                      }}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Descripción (opcional)</Field.Label>
                    <Textarea
                      rows={3}
                      placeholder="Notas del programa, lugar, fecha…"
                      value={nuevaCarpetaDescripcion}
                      onChange={(e) => setNuevaCarpetaDescripcion(e.target.value)}
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button variant="ghost" onClick={onModalCrearCarpetaClose}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleCrearCarpetaFromModal}
                  loading={carpetasMutating}><FaPlus />Crear carpeta
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={modalCambiarPwdOpen}
        placement='center'
        size='md'
        onOpenChange={e => {
          if (!e.open) {
            setCambiarActual('');
            setCambiarNueva('');
            setCambiarNueva2('');
            onModalCambiarPwdClose();
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Cambiar contraseña del enlace</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack align="stretch" gap={4}>
                  <Alert.Root
                    status="warning"
                    variant='subtle'
                    fontSize="sm"
                    borderRadius="md"
                    borderStartWidth='3px'
                    borderStartColor='colorPalette.solid'>
                    <Alert.Indicator />
                    <Box>
                      La nueva contraseña del enlace no debe ser la misma que la de tu cuenta Cadencya: así reduces el
                      riesgo de que quien conozca solo la clave del repertorio pueda probarla en tu inicio de sesión.
                    </Box>
                  </Alert.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Contraseña actual</Field.Label>
                    <Input
                      type="password"
                      value={cambiarActual}
                      onChange={(e) => setCambiarActual(e.target.value)}
                      autoComplete="current-password"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Nueva contraseña</Field.Label>
                    <Input
                      type="password"
                      value={cambiarNueva}
                      onChange={(e) => setCambiarNueva(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      autoComplete="new-password"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Repetir nueva contraseña</Field.Label>
                    <Input
                      type="password"
                      value={cambiarNueva2}
                      onChange={(e) => setCambiarNueva2(e.target.value)}
                      autoComplete="new-password"
                    />
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCambiarActual('');
                    setCambiarNueva('');
                    setCambiarNueva2('');
                    onModalCambiarPwdClose();
                  }}
                >
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={submitCambiarRepertorioSharePassword} loading={cambiarSaving}>
                  Guardar nueva contraseña
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={visorPartituraAbierto && Boolean(selectedPage)}
        size='full'
        motionPreset="none"
        onOpenChange={e => {
          if (!e.open) {
            cerrarVisorPartitura();
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop bg="black" />
          <Dialog.Positioner>
            <Dialog.Content
              m={0}
              maxW="100vw"
              w="100vw"
              h="100dvh"
              maxH="100dvh"
              bg="black"
              borderRadius={0}
              borderWidth={0}
              boxShadow="none"
              position="relative"
              overflow="hidden">
              <Dialog.Body p={0} m={0} h="100%" maxH="100dvh" overflow="hidden" position="relative">
                {selectedPage ? (
                  <>
                    <Box
                      ref={visorViewportRef}
                      position="absolute"
                      inset={0}
                      overflow="auto"
                      overscrollBehavior="none"
                      WebkitOverflowScrolling="touch"
                      css={{
                        touchAction: 'none'
                      }}
                      cursor={
                        visorZoom > 1.02
                          ? visorDragRef.current.down
                            ? 'grabbing'
                            : 'grab'
                          : 'auto'
                      }
                      onClick={(e) => {
                        // Fallback desktop / mouse: toggle chrome en móvil también vía click
                        if (e.target?.closest?.('[data-visor-chrome]')) return;
                        if (visorSwipeRef.current.moved || visorPinchRef.current.active) return;
                        if (isMobileVisor) setVisorChromeVisible((v) => !v);
                      }}
                      onMouseDown={(e) => {
                        if (visorZoom <= 1.02) return;
                        const el = e.currentTarget;
                        visorDragRef.current.down = true;
                        visorDragRef.current.startX = e.clientX;
                        visorDragRef.current.startY = e.clientY;
                        visorDragRef.current.startScrollLeft = el.scrollLeft;
                        visorDragRef.current.startScrollTop = el.scrollTop;
                      }}
                      onMouseMove={(e) => {
                        if (!visorDragRef.current.down) return;
                        const el = e.currentTarget;
                        const dx = e.clientX - visorDragRef.current.startX;
                        const dy = e.clientY - visorDragRef.current.startY;
                        el.scrollLeft = visorDragRef.current.startScrollLeft - dx;
                        el.scrollTop = visorDragRef.current.startScrollTop - dy;
                      }}
                      onMouseUp={() => {
                        visorDragRef.current.down = false;
                      }}
                      onMouseLeave={() => {
                        visorDragRef.current.down = false;
                      }}
                      onWheel={(e) => {
                        const delta = e.deltaY;
                        if (!delta) return;
                        e.preventDefault();
                        const factor = Math.exp(-delta * 0.0012);
                        setVisorZoom((z) => clampVisorZoom(z * factor));
                      }}
                      onTouchStart={(e) => {
                        if (e.touches?.length === 2) {
                          const [a, b] = e.touches;
                          const dx = a.clientX - b.clientX;
                          const dy = a.clientY - b.clientY;
                          visorPinchRef.current = {
                            active: true,
                            startDist: Math.hypot(dx, dy) || 1,
                            startZoom: visorZoom,
                          };
                          visorSwipeRef.current.tracking = false;
                          return;
                        }
                        if (e.touches?.length === 1) {
                          const t = e.touches[0];
                          visorSwipeRef.current = {
                            tracking: true,
                            startX: t.clientX,
                            startY: t.clientY,
                            startTime: Date.now(),
                            moved: false,
                            axis: null,
                            lastDx: 0,
                            lastDy: 0,
                          };
                          if (visorZoom > 1.02) {
                            const el = e.currentTarget;
                            visorDragRef.current = {
                              down: true,
                              startX: t.clientX,
                              startY: t.clientY,
                              startScrollLeft: el.scrollLeft,
                              startScrollTop: el.scrollTop,
                            };
                          }
                        }
                      }}
                      onTouchMove={(e) => {
                        if (e.touches?.length === 2 && visorPinchRef.current.active) {
                          e.preventDefault();
                          const [a, b] = e.touches;
                          const dx = a.clientX - b.clientX;
                          const dy = a.clientY - b.clientY;
                          const dist = Math.hypot(dx, dy);
                          const ratio = visorPinchRef.current.startDist
                            ? dist / visorPinchRef.current.startDist
                            : 1;
                          setVisorZoom(clampVisorZoom(visorPinchRef.current.startZoom * ratio));
                          visorSwipeRef.current.moved = true;
                          return;
                        }
                        if (e.touches?.length === 1 && visorSwipeRef.current.tracking) {
                          const t = e.touches[0];
                          const dx = t.clientX - visorSwipeRef.current.startX;
                          const dy = t.clientY - visorSwipeRef.current.startY;
                          visorSwipeRef.current.lastDx = dx;
                          visorSwipeRef.current.lastDy = dy;
                          if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
                            visorSwipeRef.current.moved = true;
                          }
                          if (!visorSwipeRef.current.axis) {
                            if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
                              visorSwipeRef.current.axis =
                                Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
                            }
                          }
                          if (visorZoom > 1.02 && visorDragRef.current.down) {
                            e.preventDefault();
                            const el = e.currentTarget;
                            el.scrollLeft = visorDragRef.current.startScrollLeft - dx;
                            el.scrollTop = visorDragRef.current.startScrollTop - dy;
                          } else if (visorSwipeRef.current.axis === 'x') {
                            e.preventDefault();
                          }
                        }
                      }}
                      onTouchEnd={(e) => {
                        if (e.target?.closest?.('[data-visor-chrome]')) {
                          visorPinchRef.current = { active: false, startDist: 0, startZoom: visorZoom };
                          visorDragRef.current.down = false;
                          visorSwipeRef.current.tracking = false;
                          return;
                        }
                        const swipe = visorSwipeRef.current;
                        const wasPinch = visorPinchRef.current.active;
                        visorPinchRef.current = { active: false, startDist: 0, startZoom: visorZoom };
                        visorDragRef.current.down = false;

                        if (!wasPinch && swipe.tracking) {
                          const dx = swipe.lastDx || 0;
                          const dy = swipe.lastDy || 0;
                          const isTap =
                            !swipe.moved || (Math.abs(dx) < 12 && Math.abs(dy) < 12);
                          if (isTap) {
                            setVisorChromeVisible((v) => !v);
                          } else if (swipe.axis === 'x' && visorZoom <= 1.08) {
                            if (dx < -40) visorIrSiguiente();
                            else if (dx > 40) visorIrAnterior();
                          }
                        }

                        // Evita que el click sintético vuelva a togglear el chrome
                        visorSwipeRef.current.moved = true;
                        visorSwipeRef.current.tracking = false;
                        setTimeout(() => {
                          visorSwipeRef.current.moved = false;
                        }, 350);
                      }}
                    >
                      <Flex
                        ref={visorScrollRef}
                        minW="100%"
                        minH="100%"
                        w={
                          visorDisplaySize
                            ? `${Math.max(visorViewportSize.w || 0, visorDisplaySize.layoutW)}px`
                            : '100%'
                        }
                        h={
                          visorDisplaySize
                            ? `${Math.max(visorViewportSize.h || 0, visorDisplaySize.layoutH)}px`
                            : '100%'
                        }
                        align="center"
                        justify="center"
                        pointerEvents="none"
                      >
                        <ChakraImage
                          src={pageImageSrc(selectedPage)}
                          alt={`Partitura orden ${selectedPage.orden}`}
                          display="block"
                          pointerEvents="none"
                          userSelect="none"
                          draggable={false}
                          maxW="none"
                          style={
                            visorDisplaySize
                              ? {
                                  width: `${visorDisplaySize.w}px`,
                                  height: `${visorDisplaySize.h}px`,
                                  transform: `rotate(${selectedPage.rotacion || 0}deg)`,
                                  transformOrigin: 'center center',
                                }
                              : {
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  transform: `rotate(${selectedPage.rotacion || 0}deg)`,
                                  transformOrigin: 'center center',
                                }
                          }
                          onLoad={(e) => {
                            const img = e.currentTarget;
                            setVisorImgNatural({
                              w: img.naturalWidth || 0,
                              h: img.naturalHeight || 0,
                            });
                          }}
                        />
                      </Flex>
                    </Box>

                    {/* Controles flotantes: desktop completo; móvil solo cerrar + nº de página */}
                    {(!isMobileVisor || visorChromeVisible) && (
                      <Box
                        data-visor-chrome
                        position="absolute"
                        inset={0}
                        pointerEvents="none"
                        zIndex={20}
                      >
                        {isMobileVisor ? (
                          <>
                            <Text
                              position="absolute"
                              left={3}
                              fontSize="xs"
                              fontWeight="600"
                              color="white"
                              bg="blackAlpha.600"
                              px={2}
                              py={1}
                              borderRadius="md"
                              pointerEvents="none"
                              letterSpacing="wide"
                              css={{
                                top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
                              }}
                            >
                              {Math.max(0, idxVisorPartitura) + 1} - {paginasOrdenadasVisor.length || paginasOrdenadasEditor.length}
                            </Text>
                            <IconButton
                              aria-label="Cerrar"
                              onClick={cerrarVisorPartitura}
                              position="absolute"
                              right={3}
                              size="md"
                              variant="solid"
                              colorPalette="blackAlpha"
                              bg="blackAlpha.700"
                              color="white"
                              pointerEvents="auto"
                              borderRadius="full"
                              css={{
                                top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
                              }}
                            >
                              <FaTimes />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              aria-label="Cerrar"
                              onClick={cerrarVisorPartitura}
                              position="absolute"
                              right={3}
                              size="md"
                              variant="solid"
                              colorPalette="blackAlpha"
                              bg="blackAlpha.700"
                              color="white"
                              pointerEvents="auto"
                              borderRadius="full"
                              css={{
                                top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
                              }}
                            >
                              <FaTimes />
                            </IconButton>

                            <IconButton
                              aria-label="Página anterior"
                              onClick={visorIrAnterior}
                              disabled={!visorPuedeAnterior}
                              position="absolute"
                              left={2}
                              top="50%"
                              transform="translateY(-50%)"
                              size={{ base: 'md', md: 'lg' }}
                              variant="solid"
                              colorPalette="blackAlpha"
                              bg="blackAlpha.600"
                              color="white"
                              pointerEvents="auto"
                              borderRadius="full"
                            >
                              <FaChevronLeft />
                            </IconButton>
                            <IconButton
                              aria-label="Página siguiente"
                              onClick={visorIrSiguiente}
                              disabled={!visorPuedeSiguiente}
                              position="absolute"
                              right={2}
                              top="50%"
                              transform="translateY(-50%)"
                              size={{ base: 'md', md: 'lg' }}
                              variant="solid"
                              colorPalette="blackAlpha"
                              bg="blackAlpha.600"
                              color="white"
                              pointerEvents="auto"
                              borderRadius="full"
                            >
                              <FaChevronRight />
                            </IconButton>

                            <Box
                              position="absolute"
                              left={0}
                              right={0}
                              bottom={0}
                              pointerEvents="auto"
                              bg="blackAlpha.700"
                              backdropFilter="blur(8px)"
                              borderTopWidth="1px"
                              borderColor="whiteAlpha.200"
                              px={{ base: 2, md: 4 }}
                              pt={2}
                              css={{
                                pb: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)',
                              }}
                            >
                              <Flex justify="center" gap={2} align="center" flexWrap="wrap">
                                <IconButton
                                  aria-label="Reducir zoom"
                                  size="sm"
                                  variant="outline"
                                  borderColor="whiteAlpha.400"
                                  color="white"
                                  onClick={() => setVisorZoom((z) => clampVisorZoom(z / 1.08))}
                                >
                                  <FaSearchMinus />
                                </IconButton>
                                <Text fontSize="xs" color="gray.200" minW="52px" textAlign="center">
                                  {Math.round(visorZoom * 100)}%
                                </Text>
                                <IconButton
                                  aria-label="Aumentar zoom"
                                  size="sm"
                                  variant="outline"
                                  borderColor="whiteAlpha.400"
                                  color="white"
                                  onClick={() => setVisorZoom((z) => clampVisorZoom(z * 1.08))}
                                >
                                  <FaSearchPlus />
                                </IconButton>
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  color="gray.300"
                                  onClick={() => setVisorZoom(1)}
                                  disabled={visorZoom === 1}
                                >
                                  Ajustar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  borderColor="whiteAlpha.400"
                                  color="white"
                                  onClick={() => void descargarPaginaObra(selectedPage)}
                                  loading={descargandoPaginaId === selectedPage?.id}
                                  disabled={!selectedPage?.imagenUrl}
                                >
                                  <FaDownload />
                                  Descargar
                                </Button>
                                {selectedPage?.obraId ? (
                                  <Button
                                    size="sm"
                                    variant="solid"
                                    colorPalette="purple"
                                    onClick={() => {
                                      const obraId = selectedPage.obraId;
                                      cerrarVisorPartitura();
                                      openObraFicha(obraId);
                                    }}
                                  >
                                    <FaEye />
                                    Ver obra
                                  </Button>
                                ) : null}
                              </Flex>
                              <Box maxW="420px" mx="auto" mt={2} display={{ base: 'none', md: 'block' }}>
                                <SliderRoot
                                  aria-label="Zoom"
                                  min={0.35}
                                  max={4}
                                  step={0.01}
                                  value={visorZoom}
                                  onValueChange={(v) => setVisorZoom(clampVisorZoom(v))}
                                >
                                  <SliderTrack bg="whiteAlpha.200">
                                    <SliderFilledTrack bg="purple.400" />
                                  </SliderTrack>
                                  <SliderThumb />
                                </SliderRoot>
                              </Box>
                              <Text textAlign="center" fontSize="sm" color="gray.400" mt={2} px={2}>
                                {selectedPage.obraTitulo ? (
                                  <>
                                    <Text as="span" color="gray.200" fontWeight="600">
                                      {selectedPage.obraTitulo}
                                    </Text>
                                    {' · '}
                                  </>
                                ) : detail?.titulo ? (
                                  <>
                                    <Text as="span" color="gray.200" fontWeight="600">
                                      {detail.titulo}
                                    </Text>
                                    {' · '}
                                  </>
                                ) : null}
                                Orden {selectedPage.orden} · {Math.max(0, idxVisorPartitura) + 1}/
                                {paginasOrdenadasVisor.length || paginasOrdenadasEditor.length}
                                <Text as="span" fontSize="xs" color="gray.500">
                                  {' '}
                                  · Flechas · Esc
                                </Text>
                              </Text>
                            </Box>
                          </>
                        )}
                      </Box>
                    )}
                  </>
                ) : null}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Dialog: advertencia páginas sin etiquetar al pasar a Listo */}
      <Dialog.Root
        open={dialogListoOpen}
        initialFocusEl={() => cancelListoRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDialogListoClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  ¿Pasar a listo igualmente?
                </Dialog.Header>
                <Dialog.Body>
                  {(() => {
                    const paginas = detail?.paginas || [];
                        const sinEtiquetar = paginas.filter((p) => !(Array.isArray(p.instrumentosVoces) && p.instrumentosVoces.length)).length;
                    return (
                      <>
                        <Alert.Root status="warning" rounded="md" mb={3}>
                          <Alert.Indicator />
                          {sinEtiquetar} de {paginas.length} página{sinEtiquetar !== 1 ? 's' : ''} no{' '}
                          {sinEtiquetar !== 1 ? 'tienen' : 'tiene'} instrumento asignado.
                        </Alert.Root>
                        <Text fontSize="sm" color="gray.600">
                          Las páginas sin etiquetar no se mostrarán correctamente en el repertorio compartido ni en las
                          partichellas. Puedes etiquetar antes desde el botón «Etiquetar páginas».
                        </Text>
                      </>
                    );
                  })()}
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button ref={cancelListoRef} onClick={onDialogListoClose}>
                    Cancelar
                  </Button>
                  <Button colorPalette="green" onClick={confirmarPasarAListo} loading={saving}>
                    Pasar a listo igualmente
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* Dialog: confirmación para volver a Borrador */}
      <Dialog.Root
        open={dialogSalidaBorradorOpen}
        initialFocusEl={() => cancelSalidaBorradorRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            salidaBorradorDestinoRef.current = null;
            onDialogSalidaBorradorClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  ¿Salir sin pasar a listo?
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm">
                    Esta obra sigue en <strong>borrador</strong>. Si sales ahora, no estará disponible en el repertorio
                    compartido.
                  </Text>
                  <Text fontSize="sm" mt={3}>
                    Para pasarla a <strong>Listo</strong>, revisa que el título, los detalles, las páginas y todas las
                    etiquetas de instrumentos y voces sean correctos. Cuando todo esté bien, usa el botón «Pasar a listo» en
                    la ficha o en el editor.
                  </Text>
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button ref={cancelSalidaBorradorRef} onClick={onDialogSalidaBorradorClose}>
                    Seguir editando
                  </Button>
                  <Button colorPalette="orange" variant="outline" onClick={confirmarSalidaObraBorrador}>
                    Salir sin pasar a listo
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <Dialog.Root
        open={dialogBorradorOpen}
        initialFocusEl={() => cancelBorradorRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDialogBorradorClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Volver a borrador
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm">
                    La obra dejará de estar disponible en el repertorio compartido hasta que la vuelvas a pasar a{' '}
                    <strong>Listo</strong>. ¿Continuar?
                  </Text>
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button ref={cancelBorradorRef} onClick={onDialogBorradorClose}>
                    Cancelar
                  </Button>
                  <Button colorPalette="orange" onClick={pasarObraABorrador} loading={saving}>
                    Volver a borrador
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <Dialog.Root
        open={dialogVaciarCarpetaOpen}
        initialFocusEl={() => cancelVaciarCarpetaRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDialogVaciarCarpetaClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Eliminar todas las obras de la carpeta
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm">
                    Se quitarán todas las obras de «{selectedCarpeta?.nombre || 'esta carpeta'}». Las obras no se borran del
                    repertorio; solo dejan de estar en esta carpeta. ¿Continuar?
                  </Text>
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button ref={cancelVaciarCarpetaRef} onClick={onDialogVaciarCarpetaClose}>
                    Cancelar
                  </Button>
                  <Button colorPalette="red" onClick={confirmarVaciarCarpeta} loading={carpetasMutating}>
                    Eliminar todas
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <Dialog.Root
        open={dialogEliminarObraOpen}
        initialFocusEl={() => cancelEliminarObraRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDialogEliminarObraClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Eliminar obra
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm" color="gray.700">
                    ¿Seguro que quieres eliminar esta obra y todas sus páginas? Esta acción no se puede deshacer.
                  </Text>
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button
                    ref={cancelEliminarObraRef}
                    onClick={() => {
                      setObraIdPendienteEliminar(null);
                      onDialogEliminarObraClose();
                    }}
                    disabled={deletingObra}
                  >
                    Cancelar
                  </Button>
                  <Button colorPalette="red" onClick={confirmarEliminarObra} loading={deletingObra}>
                    Eliminar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <Dialog.Root
        open={dialogConfirmCatalogDeleteOpen}
        initialFocusEl={() => cancelCatalogDeleteRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            if (catalogDeleteLoading) return;
            setPendingCatalogDelete(null);
            onDialogConfirmCatalogDeleteClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  {pendingCatalogDelete?.type === 'carpeta'
                    ? 'Eliminar carpeta'
                    : pendingCatalogDelete?.type === 'instrumento'
                      ? 'Eliminar instrumento'
                      : 'Eliminar voz'}
                </Dialog.Header>
                <Dialog.Body>
                  {pendingCatalogDelete?.type === 'carpeta' ? (
                    <Text fontSize="sm" color="gray.700">
                      ¿Eliminar la carpeta «{pendingCatalogDelete?.nombre || 'sin nombre'}»? Las obras no se borran; solo se quitan de esta agrupación.
                    </Text>
                  ) : pendingCatalogDelete?.type === 'instrumento' ? (
                    <Text fontSize="sm" color="gray.700">
                      ¿Eliminar el instrumento personalizado «{pendingCatalogDelete?.nombre || 'sin nombre'}»?
                    </Text>
                  ) : (
                    <Text fontSize="sm" color="gray.700">
                      ¿Quitar la voz «{pendingCatalogDelete?.nombre || 'sin nombre'}» del catálogo? Las páginas que la usen conservarán la etiqueta guardada.
                    </Text>
                  )}
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button
                    ref={cancelCatalogDeleteRef}
                    onClick={() => {
                      setPendingCatalogDelete(null);
                      onDialogConfirmCatalogDeleteClose();
                    }}
                    disabled={catalogDeleteLoading}
                  >
                    Cancelar
                  </Button>
                  <Button colorPalette="red" onClick={confirmDeleteCatalogItem} loading={catalogDeleteLoading}>
                    Eliminar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <Dialog.Root
        open={dialogCrearMaestroOpen}
        initialFocusEl={() => cancelCrearMaestroRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDialogCrearMaestroClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Nuevos instrumentos y voces detectados
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Antes de guardar el etiquetado se crearán en el maestro los siguientes elementos detectados por el auto-etiquetado:
                  </Text>
                  {pendienteCrearMaestro.instrumentos.length > 0 && (
                    <Box mb={3}>
                      <Text fontSize="sm" fontWeight="700" mb={1}>Instrumentos nuevos</Text>
                      <Wrap gap={2}>
                        {pendienteCrearMaestro.instrumentos.map((it) => (
                          <Tag.Root key={`inst-${it.slug}`} colorPalette="purple">{it.nombre}</Tag.Root>
                        ))}
                      </Wrap>
                    </Box>
                  )}
                  {pendienteCrearMaestro.voces.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="700" mb={1}>Voces nuevas</Text>
                      <Wrap gap={2}>
                        {pendienteCrearMaestro.voces.map((v) => (
                          <Tag.Root key={`voz-${v.slug}`} colorPalette="blue">{v.nombre}</Tag.Root>
                        ))}
                      </Wrap>
                    </Box>
                  )}
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button ref={cancelCrearMaestroRef} onClick={onDialogCrearMaestroClose} disabled={creandoMaestroAntesGuardar}>
                    Cancelar
                  </Button>
                  <Button colorPalette="purple" onClick={confirmarCrearMaestrosYGuardar} loading={creandoMaestroAntesGuardar}>
                    Crear y guardar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </AppLayout>
  );
}
